import TourPopover from './common/TourPopover';
import scoreBarColor from './common/scoreBarColor';
import OptimizationStepper from './common/OptimizationStepper';
import LoadingStepper from './common/LoadingStepper';
import ScoreRing from './common/ScoreRing';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import ATSRewriteOffer from './dashboard/ATSRewriteOffer';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { analysisAPI, resumeAPI, optimizeAPI, extractJDFromUrl, trackerAPI, contactAPI } from '../services/api';
import paymentAPI from '../services/paymentAPI';
import { useAuth } from '../contexts/AuthContext';
import HistoryTable from './HistoryTable';
import ValleyOfPeace from './ValleyOfPeace';
import UpgradeModal from './UpgradeModal';

const GREETINGS = [
  "Ready to build?",
  "What are we optimizing today?",
  "Let's match that role!",
  "Time to showcase your skills!",
  "Your next opportunity awaits?",
  "Ready to stand out?"
];

const CAREER_TIPS = [
  {
    title: "Leonardo's Legacy",
    text: "Ever wonder who started all this? Leonardo da Vinci wrote the first resume in 1482, bragging about his cool bridge designs. What's your unique superpower?",
    source: "Historical Archive"
  },
  {
    title: "The 7-Second Scan",
    text: "Recruiters scan resumes in an 'F' shape, taking about 7 seconds. Let's make your top two lines absolutely shine so they can't look away!",
    source: "Recruitment Usability Lab"
  },
  {
    title: "Trust in Blue",
    text: "Adding a subtle splash of blue to your layout makes you appear 18% more trustworthy. It's the psychological color of stability—cool, right?",
    source: "Color Institute Study"
  },
  {
    title: "Contextual Matching",
    text: "Modern AI doesn't just match keywords; it reads context. It understands that 'led a team' and 'steered a group' mean the same wonderful thing.",
    source: "RezFlow Innovation"
  },
  {
    title: "Evidence Wins",
    text: "Skills carry way more weight when backed by experience. Connecting your abilities directly to past achievements turns claims into solid proof.",
    source: "RezFlow Innovation"
  },
  {
    title: "Format First",
    text: "Even strong candidates get filtered out by formatting issues. A clean layout ensures your resume remains readable to screening systems and human eyes alike.",
    source: "RezFlow Design"
  },
  {
    title: "Easy to Discover",
    text: "A resume should be easy to read and search. Clear document generation creates text-searchable files that make your skills instantly discoverable.",
    source: "RezFlow Design"
  },
  {
    title: "Impact Over Duties",
    text: "Recruiters notice impact, not just job duties. Changing everyday responsibilities into measurable achievements makes a massive difference.",
    source: "RezFlow Optimization"
  },
  {
    title: "Stay Organized",
    text: "Consistent job searches rely on organization. Keeping every resume version and application in one place helps you stay focused and stress-free.",
    source: "RezFlow Workspace"
  },
  {
    title: "Words of Action",
    text: "The difference between being noticed and being skipped can be a few words. Strong action verbs convey ownership, leadership, and drive.",
    source: "RezFlow Optimization"
  },
  {
    title: "Visual Harmony",
    text: "Professional presentation matters. Keeping formatting, margins, and balanced spacing clean across every page builds immediate visual trust.",
    source: "RezFlow Design"
  },
  {
    title: "The Bullet Advantage",
    text: "A study shows that using bullet points instead of long paragraphs increases readability by 40%. Keep it punchy, clean, and easy to digest!",
    source: "Typographic Psychology Poll"
  },
  {
    title: "ATS Beginnings",
    text: "Did you know the birth of email in the late 1990s led to the creation of ATS? It was designed to help human eyes manage the new digital wave.",
    source: "HR Tech History"
  },
  {
    title: "Font Psychology",
    text: "Serif fonts like Times New Roman feel traditional and authoritative, while Sans-Serif fonts like Arial project modern, clean tech skills.",
    source: "Typographic Psychology Poll"
  },
  {
    title: "Metrics Matter",
    text: "Including measurable metrics—like percentages or dollar amounts—makes a resume 34% more memorable to hiring managers.",
    source: "HR Horror Stories"
  },
  {
    title: "Parser Security",
    text: "Avoid white-text keyword stuffing. Modern parsers detect hidden text instantly. Honesty and clear context are always your best strategies!",
    source: "ATS Security Team"
  },
  {
    title: "Concise is King",
    text: "The longest resume ever submitted was over 100 pages long! We promise we will help keep yours concise, relevant, and beautifully focused.",
    source: "HR Horror Stories"
  },
  {
    title: "Your Marketing Page",
    text: "Your resume is a marketing document, not an autobiography. Highlight the skills that directly match where you want to go next!",
    source: "RezFlow Design"
  },
  {
    title: "Breathe & Focus",
    text: "Taking a deep breath and staying positive increases cognitive function during interviews. You've got this, and we're with you all the way!",
    source: "Wellness & Success Guide"
  },
  {
    title: "Precious Metals",
    text: "In 2018, a candidate shipped their resume on pure gold-plated metal. It got a 90% callback rate, but a polished AI-optimized layout is much cheaper!",
    source: "AdWeek News"
  },
  {
    title: "VHS to PDF",
    text: "The first video resume was submitted on a VHS tape in 1984. Today, a clean, keyword-matched PDF is the gold standard for landing interviews.",
    source: "Media History Archive"
  },
  {
    title: "Tailor Your Story",
    text: "Customizing your resume for each role shows genuine interest. We make tailoring your achievements quick, painless, and highly effective.",
    source: "RezFlow Workspace"
  },
  {
    title: "White Space Magic",
    text: "A balanced layout with 30% white space is proven to reduce recruiter fatigue. Giving your achievements room to breathe makes them stand out.",
    source: "RezFlow Design"
  },
  {
    title: "Check Your Details",
    text: "Double check your contact info! 8% of candidates forget to update their phone number or email, missing out on crucial callbacks.",
    source: "Privacy Association Guide"
  },
  {
    title: "You Are More",
    text: "You are more than just a list of jobs. Your career journey is unique, and we are here to help you tell your story in the most compelling way possible.",
    source: "RezFlow Team"
  }
];

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  const [showWelcomeCreditBanner, setShowWelcomeCreditBanner] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('rzShowWelcomeCreditMessage') === '1') {
      setShowWelcomeCreditBanner(true);
      sessionStorage.removeItem('rzShowWelcomeCreditMessage');

      const timer = setTimeout(() => {
        setShowWelcomeCreditBanner(false);
      }, 6000); // auto-dismiss after 6s
      return () => clearTimeout(timer);
    }
  }, []);

  // State Management
  const [jdInput, setJdInput] = useState('');
  const [atsRewriteOffer, setAtsRewriteOffer] = useState(null);
  const [jdUrl, setJdUrl] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isPreparingUpload, setIsPreparingUpload] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState('');
  const [showUrlExtractor, setShowUrlExtractor] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [optimizationJob, setOptimizationJob] = useState(null);
  const [optimizationArtifacts, setOptimizationArtifacts] = useState([]);
  const [optimizationPreview, setOptimizationPreview] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationSuccess, setOptimizationSuccess] = useState(false);

  // Strategy (Hybrid Intelligence) state — auto-fetched after analysis
  const [sectionStrategies, setSectionStrategies] = useState([]);
  const [sectionModes, setSectionModes] = useState({});
  const [sampleMode, setSampleMode] = useState('surgical');
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(false);
  const [scoreComparison, setScoreComparison] = useState(null);
  const [showScoreBreakdown, setShowScoreBreakdown] = useState(false);
  const [expandedSkillSections, setExpandedSkillSections] = useState({});

  const combinedFacts = CAREER_TIPS;
  const [shuffledFacts, setShuffledFacts] = useState([]);
  const [activeTab, setActiveTab] = useState('input'); // 'input', 'results', 'history'
  const [isSavingToTracker, setIsSavingToTracker] = useState(false);
  const [savedToTracker, setSavedToTracker] = useState(false);
  const [skillEvidence, setSkillEvidence] = useState({});
  const [skillTargets, setSkillTargets] = useState({});
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [hasDownloadedPdf, setHasDownloadedPdf] = useState(false);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [isFooterVisible, setIsFooterVisible] = useState(false);

  const [showLowScorePopup, setShowLowScorePopup] = useState(false);
  const [forcedRewriteMode, setForcedRewriteMode] = useState(false);

  // Account dropdown & Rotating Greeting state
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isAccountDetailsModalOpen, setIsAccountDetailsModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isHelpTooltipVisible, setIsHelpTooltipVisible] = useState(false);
  const [selectedSubjectCategory, setSelectedSubjectCategory] = useState('General Question');
  const [customSubjectText, setCustomSubjectText] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [isSendingSupport, setIsSendingSupport] = useState(false);
  const [supportSuccess, setSupportSuccess] = useState('');
  const [supportError, setSupportError] = useState('');
  const [showWellnessReminder, setShowWellnessReminder] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // { credits, is_subscribed, cancel_at_period_end, current_period_end }
  const [isCancelingSub, setIsCancelingSub] = useState(false);
  const [isReactivatingSub, setIsReactivatingSub] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const accountRef = useRef(null);
  const analysisLoadingRef = useRef(null);
  const optimizationStatusRef = useRef(null);
  const previewRef = useRef(null);

  // Take a Tour state & refs
  const [tourStep, setTourStep] = useState(null); // null, 1, 2, 3
  const [showLiveModeTour, setShowLiveModeTour] = useState(false);
  const [isLiveModeTourClosing, setIsLiveModeTourClosing] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const downloadBtnRef = useRef(null);
  const saveTrackerBtnRef = useRef(null);
  const historyTabRef = useRef(null);
  const experienceSectionRef = useRef(null);

  const [isProfileHovered, setIsProfileHovered] = useState(false);
  const [hoverGreetingIndex, setHoverGreetingIndex] = useState(0);

  /* ══ Valley of Peace: Derive animation phase from workflow state ══ */
  const animationPhase = useMemo(() => {
    if (optimizationJob?.status === 'completed') return 'complete';
    if (isOptimizing) return 'optimizing';
    if (analysisResults && activeTab === 'results') return 'scored';
    if (isLoading) return 'analyzing';
    if (jdInput.trim() || resumeFile) return 'input';
    return 'idle';
  }, [optimizationJob, isOptimizing, analysisResults, activeTab, isLoading, jdInput, resumeFile]);

  const handleMouseEnterProfile = () => {
    const randomIndex = Math.floor(Math.random() * GREETINGS.length);
    setHoverGreetingIndex(randomIndex);
    setIsProfileHovered(true);
  };

  const handleMouseLeaveProfile = () => {
    setIsProfileHovered(false);
  };

  // Scroll to active tour element smoothly and quickly
  useEffect(() => {
    if (tourStep === 1) {
      // Force scroll to bottom so the sticky footer (and its buttons) fades in
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
    }
  }, [tourStep]);

  useEffect(() => {
    if (activeTab !== 'results' || !analysisResults) return;

    const userLiveTourKey = `hasSeenLiveModeTour_${user?.email || 'guest'}`;
    if (localStorage.getItem(userLiveTourKey) === 'true') return;

    const targetEl = experienceSectionRef.current;
    if (!targetEl) return;

    let timerId = null;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          if (!timerId) {
            timerId = setTimeout(() => {
              if (localStorage.getItem(userLiveTourKey) !== 'true') {
                setShowLiveModeTour(true);
              }
            }, 500);
          }
        } else {
          if (timerId) {
            clearTimeout(timerId);
            timerId = null;
          }
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(targetEl);

    return () => {
      observer.disconnect();
      if (timerId) clearTimeout(timerId);
    };
  }, [activeTab, analysisResults, user]);

  // Fetch payment status on mount; restore saved state if returning from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const returningFromPayment = params.get('payment') === 'success';
    let creditPollTimer = null;

    // Always refresh credits
    paymentAPI.getStatus()
      .then(r => {
        setPaymentStatus(r.data);

        // Safety net: if returning from payment but credits haven't landed yet
        // (webhook delayed by Cloud Run cold start), re-poll every 5s for up to 60s.
        if (returningFromPayment && !r.data.is_subscribed && (r.data.credits ?? 0) === 0) {
          let pollCount = 0;
          const maxPolls = 12; // 12 × 5s = 60s
          creditPollTimer = setInterval(async () => {
            pollCount++;
            try {
              const pollRes = await paymentAPI.getStatus();
              setPaymentStatus(pollRes.data);
              if (pollRes.data.is_subscribed || pollRes.data.credits > 0) {
                clearInterval(creditPollTimer);
                creditPollTimer = null;
              }
            } catch (_) { /* keep trying */ }
            if (pollCount >= maxPolls && creditPollTimer) {
              clearInterval(creditPollTimer);
              creditPollTimer = null;
            }
          }, 5000);
        }
      })
      .catch(() => { });

    // Restore dashboard state saved before the Stripe redirect
    if (returningFromPayment) {
      try {
        const saved = sessionStorage.getItem('rz_presave');
        if (saved) {
          const { jd, analysis, evidence, targets } = JSON.parse(saved);
          if (jd) setJdInput(jd);
          if (analysis) {
            setAnalysisResults(analysis);
            setActiveTab('results');
          }
          if (evidence && typeof evidence === 'object') setSkillEvidence(evidence);
          if (targets && typeof targets === 'object') setSkillTargets(targets);
          sessionStorage.removeItem('rz_presave');
        }
      } catch (_) { }
      // Clean up the ?payment= query param from the URL
      navigate('/dashboard', { replace: true });
    }

    return () => {
      if (creditPollTimer) clearInterval(creditPollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click outside listener for Account dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setIsAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-scroll to analysis loader when analysis starts
  useEffect(() => {
    if (isLoading) {
      analysisLoadingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isLoading]);

  const jobId = optimizationJob?.job_id;
  const jobStatus = optimizationJob?.status;

  // Auto-scroll to optimization status when optimization job starts
  useEffect(() => {
    if (jobId && (jobStatus === 'queued' || jobStatus === 'processing')) {
      optimizationStatusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [jobId, jobStatus]);

  // Auto-scroll to optimized resume preview when it becomes available
  useEffect(() => {
    if (optimizationPreview) {
      // Timeout is used to ensure the component is fully rendered/painted in DOM before scrolling
      const timer = setTimeout(() => {
        previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [optimizationPreview]);

  // Show/hide sticky footer based on scroll position (fade in near bottom)
  useEffect(() => {
    const BOTTOM_THRESHOLD = 100; // px from the bottom to trigger visibility
    const handleScroll = () => {
      const scrolledToBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - BOTTOM_THRESHOLD;
      setIsFooterVisible(scrolledToBottom);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Run once on mount in case the page is already at the bottom
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cycle resume facts on overlay every 12 seconds when loading or optimizing
  const isOverlayActive = isLoading || isExtracting || isPreparingUpload || isOptimizing;
  useEffect(() => {
    if (!isOverlayActive) return;

    // Shuffle the combinedFacts (CAREER_TIPS) dynamically
    const shuffleArray = (arr) => {
      const newArr = [...arr];
      for (let i = newArr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
      }
      return newArr;
    };

    setShuffledFacts(shuffleArray(combinedFacts));
    setCurrentFactIndex(0);

    const interval = setInterval(() => {
      setCurrentFactIndex((prevIndex) => (prevIndex + 1) % combinedFacts.length);
    }, 15000); // 15 seconds cycle timing

    return () => clearInterval(interval);
  }, [isOverlayActive, combinedFacts]);

  // Wellness reminder timer (every 20 minutes)
  useEffect(() => {
    const wellnessTimer = setInterval(() => {
      setShowWellnessReminder(true);
    }, 20 * 60 * 1000);
    return () => clearInterval(wellnessTimer);
  }, []);

  // Allowed file types
  const ALLOWED_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain'
  ];
  const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt'];

  // Extract JD from URL
  const handleExtractJD = async () => {
    if (!jdUrl.trim()) {
      setStatus('❌ Please enter a JD URL');
      return;
    }

    setIsExtracting(true);
    setLoadingPhase('extract');
    setStatus('🔄 Extracting JD from URL...');
    try {
      const extracted = await extractJDFromUrl(jdUrl);
      setJdInput(extracted);
      setStatus('✅ JD extracted successfully');
    } catch (error) {
      setStatus(`❌ ${error.message}`);
    } finally {
      setIsExtracting(false);
      setLoadingPhase('');
    }
  };

  // Validate and handle file selection
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsPreparingUpload(true);
    setLoadingPhase('upload');

    // Validate file type
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const isValidType = ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(fileExtension);

    if (!isValidType) {
      setStatus(`❌ Invalid file type. Allowed: PDF, DOC, DOCX, ODT, TXT`);
      setResumeFile(null);
      setFileName('');
      setIsPreparingUpload(false);
      setLoadingPhase('');
      return;
    }

    // Validate file size (max 5MB to match backend)
    if (file.size > 5 * 1024 * 1024) {
      setStatus('❌ File size must be less than 5MB');
      setResumeFile(null);
      setFileName('');
      setIsPreparingUpload(false);
      setLoadingPhase('');
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 600));

    setResumeFile(file);
    setFileName(file.name);
    setStatus(`✅ Resume selected: ${file.name}`);
    setIsPreparingUpload(false);
    setLoadingPhase('');
  };

  // Start resume analysis
  const handleAnalyze = async () => {
    // Validation
    if (!jdInput.trim()) {
      setStatus('❌ Please provide a Job Description');
      return;
    }

    if (!resumeFile) {
      setStatus('❌ Please upload a resume');
      return;
    }

    if (jdInput.trim().length < 50) {
      setStatus('❌ Job Description seems too short. Please provide more details.');
      return;
    }

    setIsLoading(true);
    setLoadingPhase('upload');
    setStatus('📤 Uploading resume file...');

    try {
      // Clear previous optimization states when starting a new analysis
      setOptimizationJob(null);
      setOptimizationArtifacts([]);
      setOptimizationPreview(null);
      setSkillEvidence({});
      setSkillTargets({});
      setSavedToTracker(false);
      setScoreComparison(null);
      setShowScoreBreakdown(false);

      // Step 1: Upload resume (takes 12s visually)
      const resumeFormData = new FormData();
      resumeFormData.append('file', resumeFile);
      const uploadPromise = resumeAPI.upload(resumeFormData);

      await new Promise((resolve) => setTimeout(resolve, 12000));
      const uploadResponse = await uploadPromise;
      const resumeId = uploadResponse.data.resume_id;

      if (uploadResponse.data.status === 'needs_ats_rewrite') {
        setIsLoading(false);
        setLoadingPhase('');
        setStatus('');
        setAtsRewriteOffer({ resumeFile, resumeId });
        return;
      }

      await continueAnalysis(resumeId, jdInput);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Analysis failed. Please try again.';
      setStatus(`❌ ${errorMsg}`);
      setIsLoading(false);
      setLoadingPhase('');
    }
  };

  const handleRewriteComplete = async (newFile) => {
    setAtsRewriteOffer(null);
    setResumeFile(newFile);
    setFileName(newFile.name);
    setIsLoading(true);
    try {
      const resumeFormData = new FormData();
      resumeFormData.append('file', newFile);
      const uploadResponse = await resumeAPI.upload(resumeFormData);
      await continueAnalysis(uploadResponse.data.resume_id, jdInput);
    } catch (e) {
      setStatus(`❌ Failed to upload rewritten resume`);
      setIsLoading(false);
    }
  };

  const handleDeclineRewrite = async () => {
    setAtsRewriteOffer(null);
    await continueAnalysis(atsRewriteOffer.resumeId, jdInput);
  };

  const continueAnalysis = async (resumeId, jdInputToUse) => {
    try {
      setAtsRewriteOffer(null);
      setIsLoading(true);

      // Step 2: Parsing document structure (takes 12s)
      setLoadingPhase('parse');
      setStatus('📄 Parsing document structure...');
      await new Promise((resolve) => setTimeout(resolve, 12000));

      // Step 3: Running AI keyword alignment (takes 12s)
      setLoadingPhase('analyze');
      setStatus('🤖 Running AI keyword alignment...');
      const analyzePromise = analysisAPI.score(resumeId, jdInputToUse);

      await new Promise((resolve) => setTimeout(resolve, 12000));
      const analysisResponse = await analyzePromise;

      // Step 4: Compiling results dashboard (takes 12s)
      setLoadingPhase('complete');
      setStatus('✨ Compiling results dashboard...');
      await new Promise((resolve) => setTimeout(resolve, 12000));

      // Display results
      setAnalysisResults(analysisResponse.data);
      setStatus('✅ Analysis complete!');
      setActiveTab('results');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Analysis failed. Please try again.';
      setStatus(`❌ ${errorMsg}`);
    } finally {
      setIsLoading(false);
      setLoadingPhase('');
    }
  };

  // Reset form
  const handleReset = () => {
    setJdInput('');
    setJdUrl('');
    setResumeFile(null);
    setFileName('');
    setStatus('');
    setShowUrlExtractor(false);
    setIsExtracting(false);
    setIsPreparingUpload(false);
    setLoadingPhase('');
    setAnalysisResults(null);
    setOptimizationJob(null);
    setOptimizationArtifacts([]);
    setOptimizationPreview(null);
    setIsOptimizing(false);
    setSkillEvidence({});
    setSkillTargets({});
    setActiveTab('input');
    setSavedToTracker(false);
    setScoreComparison(null);
    setShowScoreBreakdown(false);
  };

  const handleClearJD = () => {
    setJdInput('');
    setJdUrl('');
  };

  const handleClearResume = () => {
    setResumeFile(null);
    setFileName('');
  };

  const toAbsoluteDownloadUrl = (downloadUrl) => {
    if (!downloadUrl) return '#';
    if (downloadUrl.startsWith('http://') || downloadUrl.startsWith('https://')) {
      return downloadUrl;
    }
    return `${api.defaults.baseURL}${downloadUrl}`;
  };

  const visibleArtifacts = optimizationArtifacts.filter((artifact) => {
    const kind = String(artifact?.kind || '').toLowerCase();
    return kind === 'docx';
  });

  // const artifactIcon = (kind) => {
  //   const normalized = String(kind || '').toLowerCase();
  //   if (normalized === 'pdf') return 'PDF';
  //   if (normalized === 'docx') return 'DOC';
  //   return 'FILE';
  // };

  const experienceSections = React.useMemo(() => {
    const rawSections = analysisResults?.raw?.ai_score?.extracted_resume_data?.experience_sections;
    if (!Array.isArray(rawSections)) return [];

    const isValidSection = (sec) => {
      const title = (sec?.title || "").trim();
      if (!title) return false;

      // 1. Filter out sections with no capital letters at all (real titles are title-cased)
      if (!/[A-Z]/.test(title)) return false;

      // 2. Filter out sections starting with a lowercase letter
      if (/^[a-z]/.test(title)) return false;

      // 3. Filter out very short words
      if (title.length < 2) return false;

      // 4. Check if title is just years/dates
      if (/^\d{4}(?:\s+\d{4})?$/.test(title)) return false;

      // 5. Check if title is just location
      const locKeywords = new Set(["bengaluru", "india", "bangalore", "new york", "san francisco", "remote", "hybrid"]);
      const words = title.toLowerCase().match(/\w+/g) || [];
      if (words.length > 0 && words.every(w => locKeywords.has(w))) return false;

      // 6. Filter out technology stacks (e.g. "AI, Computer Vision, IoT" or "Full-Stack, AI")
      // eslint-disable-next-line no-useless-escape
      const parts = title.split(/[,\/|]/);
      if (parts.length > 3) {
        const techKeywords = new Set(["ai", "iot", "cv", "nlp", "ui", "ux", "ml", "dl", "api", "db", "aws", "gcp", "ci", "cd", "cnn", "rnn", "llm", "computer vision", "computer-vision"]);
        const isStack = parts.every(part => {
          const p = part.trim().toLowerCase();
          return techKeywords.has(p);
        });
        if (isStack) return false;
      }

      // 7. Check if title is a bullet starting with a verb
      const verbs = new Set(["implemented", "trained", "built", "designed", "developed", "created", "worked", "managed", "led", "coordinated", "delivered", "integrated", "optimized", "enhanced", "automated"]);
      const firstWord = title.toLowerCase().split(/\s+/)[0] || "";
      if (verbs.has(firstWord) && title.length > 25) return false;

      // 8. Filter out sections that look like educational background (schools/colleges/universities)
      const eduKeywords = new Set(["school", "college", "university", "institute", "academy", "technological", "studies", "education"]);
      const companyWords = (sec?.company || "").toLowerCase().match(/\w+/g) || [];
      const titleWords = title.toLowerCase().match(/\w+/g) || [];
      const hasEduWord = companyWords.some(w => eduKeywords.has(w)) || titleWords.some(w => eduKeywords.has(w));
      if (hasEduWord) {
        const studentKeywords = new Set(["student", "education", "degree", "bachelor", "master", "phd", "btech", "mtech", "study", "studies"]);
        const isStudent = titleWords.some(w => studentKeywords.has(w)) || title.toLowerCase().includes("student");
        const hasInstituteTech = (sec?.company || "").toLowerCase().includes("institute") || (sec?.company || "").toLowerCase().includes("technology") || title.toLowerCase().includes("institute") || title.toLowerCase().includes("technology");
        if (isStudent || hasInstituteTech) return false;
      }

      return true;
    };

    return rawSections
      .filter(isValidSection)
      .map((section, idx) => ({
        sectionId: String(section?.section_id || '').trim() || `experience-${idx + 1}`,
        sectionType: section?.section_type || (String(section?.section_id || '').startsWith('project-') ? 'project' : 'work'),
        isProject: Boolean(section?.is_project),
        title: String(section?.title || '').trim(),
        company: String(section?.company || '').trim(),
        dateRange: String(section?.date_range || '').trim(),
        bullets: Array.isArray(section?.bullets)
          ? section.bullets.map((bullet) => String(bullet || '').trim()).filter(Boolean)
          : [],
      }))
      .filter((section) => section.sectionId || section.title || section.company || section.bullets.length > 0);
  }, [analysisResults]);

  const isProjectSection = React.useCallback((sec) => {
    if (sec.sectionType === 'project' || sec.isProject === true) return true;
    if (sec.sectionType === 'work' || sec.isProject === false) return false;
    if (sec.sectionId && sec.sectionId.startsWith('project-')) return true;
    const compLower = (sec.company || '').toLowerCase();
    return (
      compLower === 'personal/academic project' ||
      compLower.includes('academic project') ||
      compLower.includes('personal project') ||
      compLower.includes('course project') ||
      compLower.includes('capstone project')
    );
  }, []);

  const workSections = React.useMemo(() => {
    return experienceSections.filter((sec) => !isProjectSection(sec));
  }, [experienceSections, isProjectSection]);

  const projectSections = React.useMemo(() => {
    return experienceSections.filter((sec) => isProjectSection(sec));
  }, [experienceSections, isProjectSection]);



  const toggleSkillSelection = (sectionId, skill, checked) => {
    const key = `${sectionId}::${skill}`;
    setSkillEvidence((prev) => {
      const updated = { ...prev };
      if (!checked) {
        delete updated[key];
      } else {
        updated[key] = prev[key] !== undefined ? prev[key] : '';
      }
      return updated;
    });
  };

  const updateSkillExperience = (sectionId, skill, value) => {
    const key = `${sectionId}::${skill}`;
    setSkillEvidence((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const improvementItems = React.useMemo(() => {
    const recommendations = analysisResults?.recommendations || [];

    return recommendations
      .map((rec, idx) => {
        if (!rec) return null;

        let extractedSkill = null;
        let bulletText = "";
        let relevantSections = [];

        if (typeof rec === 'object' && rec !== null) {
          extractedSkill = rec.skill || rec.skill_name || null;
          bulletText = rec.bullet || rec.suggested_bullet_template || "";
          relevantSections = rec.relevant_section_ids || rec.relevantSections || [];
        } else {
          const text = String(rec || '').trim();
          if (!text) return null;
          bulletText = text;

          // Pattern 1: Gemini format
          const missingSkillMatch = text.match(/^Missing\s+(?:hard|soft)\s+skill:\s*(.+)$/i);
          if (missingSkillMatch) {
            const rawSkill = missingSkillMatch[1].trim();
            extractedSkill = rawSkill.replace(/\s*\([^)]*\)\s*$/, '').trim();
          }

          // Pattern 2: Backend synthesized format
          if (!extractedSkill) {
            const colonIdx = text.lastIndexOf(': ');
            if (colonIdx !== -1) {
              const candidate = text.slice(colonIdx + 2).trim();
              if (candidate && candidate.length < 60 && !candidate.includes('. ')) {
                extractedSkill = candidate;
              }
            }
          }
        }

        return {
          id: `improvement-${idx}`,
          text: typeof bulletText === 'object' ? JSON.stringify(bulletText) : String(bulletText),
          skill: (extractedSkill && typeof extractedSkill === 'object') ? (extractedSkill.skill || extractedSkill.skill_name || null) : (extractedSkill ? String(extractedSkill) : null),
          relevantSections: Array.isArray(relevantSections) ? relevantSections : [],
        };
      })
      .filter(Boolean);
  }, [analysisResults]);

  const buildClaimedSkillsPayload = () => {
    return Object.entries(skillEvidence)
      .map(([key, experience]) => {
        const separatorIdx = key.indexOf('::');
        if (separatorIdx === -1) {
          const skill = key;
          return {
            skill: String(skill || '').trim(),
            experience: String(experience || '').trim(),
            ...(skillTargets[skill]?.targetSectionId
              ? {
                target_section_id: skillTargets[skill].targetSectionId,
                target_section_label: skillTargets[skill].targetSectionLabel,
              }
              : {}),
          };
        }
        const sectionId = key.slice(0, separatorIdx);
        const skill = key.slice(separatorIdx + 2);
        const section = experienceSections.find(s => s.sectionId === sectionId);
        const sectionLabel = section ? [section.title, section.company].filter(Boolean).join(' - ') : '';
        return {
          skill: String(skill || '').trim(),
          experience: String(experience || '').trim(),
          ...(sectionId ? {
            target_section_id: sectionId,
            target_section_label: sectionLabel,
          } : {})
        };
      })
      .filter((entry) => entry && entry.skill);
  };

  const claimedSkillTrace = React.useMemo(() => {
    const raw = optimizationPreview?.claimed_skills_used;
    if (!Array.isArray(raw)) return [];

    return raw
      .map((item) => ({
        skill: String(item?.skill || '').trim(),
        applied: Boolean(item?.applied),
        reason: String(item?.reason || '').trim(),
        targetSectionId: String(item?.target_section_id || '').trim(),
        targetSectionLabel: String(item?.target_section_label || '').trim(),
        outputLocations: Array.isArray(item?.output_locations)
          ? item.output_locations.map((loc) => String(loc || '').trim()).filter(Boolean)
          : [],
      }))
      .filter((item) => item.skill);
  }, [optimizationPreview]);

  const describeOptimizationStep = React.useCallback((step, status) => {
    const normalizedStep = String(step || '').trim().toLowerCase();
    const normalizedStatus = String(status || '').trim().toLowerCase();

    if (normalizedStatus === 'completed') {
      return {
        title: 'Optimization complete',
        detail: 'Your optimized resume package is ready to review and download.',
      };
    }

    if (normalizedStatus === 'failed') {
      return {
        title: 'Optimization paused',
        detail: 'Something interrupted the run. You can retry and continue from here.',
      };
    }

    const stepCopy = {
      queued: {
        title: 'Getting ready',
        detail: 'Warming up your session...',
      },
      loading_analysis: {
        title: 'Reviewing the plan',
        detail: 'Aligning with your selected skills...',
      },
      optimizing_resume: {
        title: 'Polishing your experience',
        detail: 'Improving bullets in your voice...',
      },
      generating_documents: {
        title: 'Creating your documents',
        detail: 'Formatting files for download...',
      },
      completed: {
        title: 'All set!',
        detail: 'Your resume is ready.',
      },
    };

    return stepCopy[normalizedStep] || {
      title: 'Processing optimization',
      detail: 'Applying your selected improvements and assembling the final output.',
    };
  }, []);

  const optimizationStatusView = React.useMemo(() => {
    if (!optimizationJob) return null;

    const progress = Number(optimizationJob.progress || 0);
    const safeProgress = Number.isFinite(progress) ? Math.min(100, Math.max(0, progress)) : 0;
    const stepInfo = describeOptimizationStep(optimizationJob.current_step, optimizationJob.status);
    const rawStep = String(optimizationJob.current_step || 'queued').trim() || 'queued';
    const normalizedRawStep = rawStep.toLowerCase();
    const stageOrder = ['queued', 'loading_analysis', 'optimizing_resume', 'generating_documents', 'completed'];

    let stageIndex = stageOrder.indexOf(normalizedRawStep);
    if (optimizationJob.status === 'completed') stageIndex = stageOrder.length - 1;
    if (stageIndex < 0) stageIndex = 0;

    return {
      ...stepInfo,
      progress: safeProgress,
      rawStep,
      isActive: optimizationJob.status === 'queued' || optimizationJob.status === 'processing',
      stageIndex,
    };
  }, [optimizationJob, describeOptimizationStep]);

  // Auto-fetch section strategies when analysis results arrive
  useEffect(() => {
    if (!analysisResults?.analysis_id) {
      setSectionStrategies([]);
      setSectionModes({});
      return;
    }
    let cancelled = false;
    const fetchStrategies = async () => {
      setIsLoadingStrategies(true);
      try {
        const response = await optimizeAPI.analyzeStrategy(analysisResults.analysis_id);
        if (cancelled) return;
        const strategies = response.data.section_strategies || [];
        setSectionStrategies(strategies);
        const initialModes = {};
        strategies.forEach(s => {
          initialModes[s.section_id] = s.recommendation;
        });
        setSectionModes(initialModes);
      } catch (err) {
        // Silent fail — strategies are optional; sections still render normally
        console.warn('Strategy analysis fetch failed:', err.message);
      } finally {
        if (!cancelled) setIsLoadingStrategies(false);
      }
    };
    fetchStrategies();
    return () => { cancelled = true; };
  }, [analysisResults?.analysis_id]);

  useEffect(() => {
    if (analysisResults?.scores?.overall != null) {
      if (analysisResults.scores.overall < 40) {
        setShowLowScorePopup(true);
      } else {
        setShowLowScorePopup(false);
      }
      setForcedRewriteMode(false);
    }
  }, [analysisResults?.analysis_id, analysisResults?.scores?.overall]);

  const handleOptimizeResume = async (jobIdToRetry = null) => {
    if (!analysisResults?.analysis_id) {
      setStatus('❌ Analyze resume first before optimization.');
      return;
    }

    setIsOptimizing(true);
    setLoadingPhase('optimize');
    setOptimizationSuccess(false);
    setOptimizationArtifacts([]);
    setOptimizationPreview(null);
    setStatus('⚙️ Creating optimization job...');

    // Synchronously set initial job state to prevent LoadingStepper fallback
    setOptimizationJob({
      job_id: 'pending',
      status: 'queued',
      progress: 0,
      current_step: 'queued',
    });

    try {
      const claimedSkills = buildClaimedSkillsPayload();
      const completeSectionModes = {};
      workSections.forEach(sec => {
        completeSectionModes[sec.sectionId] = sectionModes[sec.sectionId] || (sectionStrategies.find(s => s.section_id === sec.sectionId)?.recommendation) || 'surgical';
      });
      projectSections.forEach(sec => {
        completeSectionModes[sec.sectionId] = sectionModes[sec.sectionId] || (sectionStrategies.find(s => s.section_id === sec.sectionId)?.recommendation) || 'surgical';
      });

      const jobResponse = jobIdToRetry
        ? await optimizeAPI.retryJob(jobIdToRetry)
        : await optimizeAPI.createJob(
          analysisResults.analysis_id,
          jdInput,
          'professional',
          claimedSkills,
          Object.keys(completeSectionModes).length > 0 ? completeSectionModes : null
        );

      const jobId = jobResponse.data.job_id;

      // Start visual progress tracking
      const startTime = Date.now();
      let currentVisualProgress = 0;
      let isRealJobDone = false;
      let finalResultData = null;
      let currentStep = 'queued';

      setOptimizationJob({
        job_id: jobId,
        status: 'queued',
        progress: 0,
        current_step: 'queued',
      });

      const visualInterval = setInterval(() => {
        if (isRealJobDone) {
          if (currentVisualProgress < 100) {
            currentVisualProgress += 5; // fast catch-up when finished
            if (currentVisualProgress > 100) currentVisualProgress = 100;
          } else {
            clearInterval(visualInterval);
            return;
          }
        } else {
          // Paced over 48 seconds (increment by 0.20% every 100ms hits 96% at 48 seconds)
          if (currentVisualProgress < 96) {
            currentVisualProgress += 0.20;
            if (currentVisualProgress > 96) currentVisualProgress = 96;
          }
        }

        setOptimizationJob({
          job_id: jobId,
          status: isRealJobDone && currentVisualProgress >= 100 ? 'completed' : 'processing',
          progress: Math.floor(currentVisualProgress),
          current_step: currentStep,
        });
      }, 100);

      const resultData = await optimizeAPI.waitForCompletion(
        jobId,
        (jobStatus) => {
          currentStep = jobStatus.current_step;
          const stepCopy = describeOptimizationStep(jobStatus.current_step, jobStatus.status);
          setStatus(`✨ ${stepCopy.title}`);
        }
      );

      // Enforce minimum 48 seconds
      const elapsed = Date.now() - startTime;
      if (elapsed < 48000) {
        await new Promise(resolve => setTimeout(resolve, 48000 - elapsed));
      }

      isRealJobDone = true;
      finalResultData = resultData;

      // Wait for visual progress to complete the catch-up to 100%
      while (currentVisualProgress < 100) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      setOptimizationSuccess(true);
      await new Promise(resolve => setTimeout(resolve, 4000));
      setOptimizationSuccess(false);

      setOptimizationArtifacts(finalResultData.artifacts || []);
      setOptimizationPreview(finalResultData.optimized_resume || null);
      // Extract score comparison for before/after card
      const comparison = finalResultData.optimized_resume?.score_comparison;
      console.log('[ScoreComparison DEBUG] optimized_resume keys:', Object.keys(finalResultData.optimized_resume || {}));
      console.log('[ScoreComparison DEBUG] comparison object:', JSON.stringify(comparison, null, 2));
      if (comparison) {
        setScoreComparison(comparison);
        console.log('[ScoreComparison DEBUG] State set successfully');
      } else {
        console.warn('[ScoreComparison DEBUG] comparison is falsy, card will NOT render');
      }
      setStatus('✅ Optimized resume is ready. Download the DOCX file below.');
      setSavedToTracker(false);
      // Refresh payment status after a successful optimization (credit was spent)
      paymentAPI.getStatus().then(r => setPaymentStatus(r.data)).catch(() => { });

      console.log('[Tour debug] optimization finished, checking tour...');
      const userTourKey = `hasSeenSaveTrackerTour_${user?.email || 'guest'}`;
      const hasSeenTour = localStorage.getItem(userTourKey) === 'true';
      console.log('[Tour debug] userTourKey:', userTourKey, 'hasSeenTour:', hasSeenTour);
      if (!hasSeenTour) {
        console.log('[Tour debug] Scheduling setTourStep(1) in 300ms');
        setTimeout(() => {
          console.log('[Tour debug] Executing setTourStep(1)');
          setTourStep(1);
        }, 300);
      }
    } catch (error) {
      // 402 = insufficient credits — open upgrade modal
      if (error.response?.status === 402 || error.response?.data?.detail === 'insufficient_credits') {
        setIsUpgradeModalOpen(true);
        setStatus('');
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Optimization failed.';
        setStatus(`❌ ${errorMsg}`);
      }
      setOptimizationJob(null);
    } finally {
      setIsOptimizing(false);
      setLoadingPhase('');
    }
  };

  const handleDownloadPdf = async (jobId) => {
    if (!jobId || isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    setHasDownloadedPdf(false);
    console.log('[PDF] Requesting PDF conversion for job:', jobId);
    try {
      console.log('[PDF] Sending request to backend — DOCX→PDF conversion starting...');
      const response = await optimizeAPI.downloadPdf(jobId);
      console.log('[PDF] Conversion complete. Received', response.data?.size ?? response.data?.byteLength ?? '?', 'bytes');
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const docxArtifact = optimizationArtifacts?.find((a) => String(a?.kind || '').toLowerCase() === 'docx');
      let filename = docxArtifact?.file_name
        ? docxArtifact.file_name.replace(/\.docx$/i, '.pdf')
        : `optimized_resume_${jobId.slice(0, 8)}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      link.download = filename;
      document.body.appendChild(link);

      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setHasDownloadedPdf(true);
      console.log('[PDF] File download triggered successfully.');
    } catch (error) {
      console.error('[PDF] Failed to download PDF:', error);
      alert('Failed to generate and download PDF. Please try again.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const showResultsTopStatus = Boolean(status) && !(isOptimizing || isSavingToTracker);
  const showResultsTopLoading = isLoading || isExtracting || isPreparingUpload;

  const handleSaveToTracker = async () => {
    if (!optimizationJob?.job_id) return;
    setIsSavingToTracker(true);
    setLoadingPhase('saveTracker');
    setStatus('💾 Saving to Application Tracker...');
    try {
      await trackerAPI.saveSnapshot(optimizationJob.job_id);
      setSavedToTracker(true);
      setStatus('✅ Saved to Application Tracker!');
      if (tourStep === 2) {
        setShowConfetti(true);
        setTimeout(() => {
          setShowConfetti(false);
          setTourStep(3);
        }, 2200);
      }
    } catch (error) {
      setStatus('❌ Failed to save to tracker.');
    } finally {
      setIsSavingToTracker(false);
      setLoadingPhase('');
    }
  };

  // Logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Subscription management
  const handleCancelSubscription = async () => {
    setIsCancelingSub(true);
    try {
      await paymentAPI.cancelSubscription();
      // Refresh payment status to pick up cancel_at_period_end
      const r = await paymentAPI.getStatus();
      setPaymentStatus(r.data);
      setShowCancelConfirm(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel subscription.');
    } finally {
      setIsCancelingSub(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setIsReactivatingSub(true);
    try {
      await paymentAPI.reactivateSubscription();
      const r = await paymentAPI.getStatus();
      setPaymentStatus(r.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reactivate subscription.');
    } finally {
      setIsReactivatingSub(false);
    }
  };

  const handleSendSupport = async (e) => {
    if (e) e.preventDefault();
    setIsSendingSupport(true);
    setSupportSuccess('');
    setSupportError('');

    const finalSubject = selectedSubjectCategory === 'Other'
      ? customSubjectText.trim()
      : selectedSubjectCategory;

    if (!finalSubject) {
      setSupportError('Please specify a subject.');
      setIsSendingSupport(false);
      return;
    }

    try {
      await contactAPI.sendEmail(finalSubject, supportMessage);
      setSupportSuccess('Your message has been sent successfully!');
      setSelectedSubjectCategory('General Question');
      setCustomSubjectText('');
      setSupportMessage('');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send your request. Please try again later.';
      setSupportError(detail);
    } finally {
      setIsSendingSupport(false);
    }
  };

  const handleCloseSupportModal = () => {
    setIsSupportModalOpen(false);
    setSelectedSubjectCategory('General Question');
    setCustomSubjectText('');
    setSupportMessage('');
    setSupportSuccess('');
    setSupportError('');
  };

  return (
    <div className={`app-container ${tourStep ? `tour-step-${tourStep}-active` : ''}`}>
      {/* Valley of Peace — Narrative Background Animation */}
      <ValleyOfPeace phase={animationPhase} />
      {/* Top Left Brand Logo */}
      <div className="brand-header">
        <span className="brand-header-logo" aria-hidden="true">⬡</span>
        <span className="brand-header-name">RezFlow</span>
      </div>

      {/* Top Right Account Section */}
      <div className="logout-header" ref={accountRef}>
        <button
          onClick={() => setIsAccountOpen(!isAccountOpen)}
          onMouseEnter={handleMouseEnterProfile}
          onMouseLeave={handleMouseLeaveProfile}
          style={styles.profileTrigger}
          aria-haspopup="true"
          aria-expanded={isAccountOpen}
          aria-label="Account Settings"
          className="hover-lift"
        >
          {user?.profile_picture ? (
            <img src={user.profile_picture} alt="Profile" style={styles.profileAvatar} />
          ) : (
            <div style={styles.profileInitialsAvatar}>
              {user ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() : 'U'}
            </div>
          )}
        </button>

        {/* Custom Hover Greeting Chip */}
        {isProfileHovered && !isAccountOpen && (
          <div className="animate-greeting" style={styles.profileHoverTooltip}>
            <div style={styles.tooltipArrow}></div>
            Hi, {user?.firstName || 'User'}! {GREETINGS[hoverGreetingIndex]}
          </div>
        )}

        {isAccountOpen && (
          <div className="account-dropdown-card" style={styles.accountDropdown}>
            <div style={styles.accountDropdownHeader}>
              <div style={styles.dropdownAvatarLarge}>
                {user?.profile_picture ? (
                  <img src={user.profile_picture} alt="Profile" style={styles.profileAvatarLarge} />
                ) : (
                  <div style={styles.profileInitialsAvatarLarge}>
                    {user ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() : 'U'}
                  </div>
                )}
              </div>
              <div style={styles.dropdownUserInfo}>
                <div style={styles.dropdownUserName}>{user ? `${user.firstName} ${user.lastName}` : 'Guest User'}</div>
                <div style={styles.dropdownUserEmail}>{user?.email || ''}</div>
              </div>
            </div>

            <div style={styles.dropdownDivider}></div>

            <div style={styles.dropdownSection}>
              <button
                onClick={() => { setIsAccountDetailsModalOpen(true); setIsAccountOpen(false); }}
                style={modalStyles.dropdownActionBtn}
                className="hover-lift"
              >
                Account Details
              </button>
              <button
                onClick={() => { setIsSupportModalOpen(true); setIsAccountOpen(false); }}
                style={modalStyles.dropdownActionBtn}
                className="hover-lift"
              >
                Contact Support
              </button>
              <button
                onClick={() => { setIsUpgradeModalOpen(true); setIsAccountOpen(false); }}
                style={modalStyles.dropdownActionBtn}
                className="hover-lift"
              >
                {paymentStatus?.is_subscribed
                  ? '✅ Pro Subscriber'
                  : `⬡ ${paymentStatus?.credits ?? '…'} credit${paymentStatus?.credits !== 1 ? 's' : ''} left`}
              </button>
            </div>

            <div style={styles.dropdownDivider}></div>

            <button onClick={handleLogout} style={styles.dropdownLogoutBtn}>
              Sign Out
            </button>
          </div>
        )}
      </div>

      {/* Floating Top Pill Nav */}
      <div className="nav-pill-bar">
        <div style={styles.navMenu}>
          <button
            onClick={() => { setActiveTab('input'); setStatus(''); }}
            style={activeTab === 'input' ? styles.navBtnActive : styles.navBtn}
          >
            Workspace
          </button>
          <button
            ref={historyTabRef}
            onClick={() => { setActiveTab('history'); setStatus(''); }}
            className={tourStep === 3 ? 'tour-highlight-pulse' : ''}
            style={activeTab === 'history' ? styles.navBtnActive : styles.navBtn}
          >
            History Tracker
          </button>
          {analysisResults && (
            <button
              onClick={() => { setActiveTab('results'); setStatus(''); }}
              style={activeTab === 'results' ? styles.navBtnActive : styles.navBtn}
            >
              Results
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="stage-container">
        {showWellnessReminder && (
          <div className="wellness-banner animate-greeting" style={styles.wellnessBanner}>
            <span style={{ fontSize: '16px', marginRight: '8px' }}>💧</span>
            <span style={{ flex: 1 }}>Time to take a break, walk around, and sip some water!</span>
            <button onClick={() => setShowWellnessReminder(false)} style={styles.wellnessCloseBtn}>✕</button>
          </div>
        )}

        {showWelcomeCreditBanner && (
          <div style={styles.modalOverlay}>
            <div className="welcome-modal-pop" style={styles.welcomeCreditModal}>
              <div className="confetti-particle p1" />
              <div className="confetti-particle p2" />
              <div className="confetti-particle p3" />
              <div className="confetti-particle p4" />
              <div className="confetti-particle p5" />
              <div style={styles.modalEmoji}>👋✨</div>
              <h2 style={styles.modalTitle}>We're Glad to Have You Here!</h2>
              <p style={styles.modalText}>
                Welcome to RezFlow! We're excited to support you on your career journey. To get you started, we've added <strong>1 free premium credit</strong> to your account. Let's make your resume stand out and land that next opportunity together!
              </p>
              <button onClick={() => setShowWelcomeCreditBanner(false)} style={styles.modalCloseBtn}>
                Start Your Journey! ✨
              </button>
            </div>
          </div>
        )}

        {activeTab === 'input' && (
          <div className="stage-card">
            <h2 style={styles.stageTitle}>Resume Optimizer</h2>
            <p style={styles.stageSubtitle}>
              Add a job description. Drop your resume. Let our AI handle the rest to make you stand out.
            </p>

            <div className="stage-grid">
              {/* Left Column: Job Description */}
              <div style={styles.stageLeftColumn}>
                <div style={styles.stageSectionHeader}>
                  <h3 style={styles.stageSectionTitle}>Job Description</h3>
                  {jdInput.trim() ? (
                    <button onClick={handleClearJD} style={styles.clearBtn}>Clear</button>
                  ) : (
                    <span style={styles.statusBadgeRequired}>Required</span>
                  )}
                </div>

                {/* URL Extractor */}
                <div style={styles.extractorContainer}>
                  <button
                    type="button"
                    style={styles.collapsibleBtn}
                    onClick={() => setShowUrlExtractor((prev) => !prev)}
                  >
                    {showUrlExtractor ? '▼' : '▶'} Extract from URL
                  </button>

                  {showUrlExtractor && (
                    <div style={styles.urlSection}>
                      <div className="url-extractor-group" style={styles.urlInputGroup}>
                        <input
                          type="text"
                          placeholder="Paste JD URL here..."
                          value={jdUrl}
                          onChange={(e) => setJdUrl(e.target.value)}
                          style={styles.stageInput}
                          disabled={isLoading || isExtracting}
                        />
                        <button
                          onClick={handleExtractJD}
                          style={styles.extractBtn}
                          disabled={isLoading || isExtracting}
                        >
                          {isExtracting ? 'Extracting...' : 'Extract'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <label style={styles.hiddenLabel} htmlFor="jd-textarea">Job Description *</label>
                <textarea
                  id="jd-textarea"
                  placeholder="Copy and paste the full job description here..."
                  value={jdInput}
                  onChange={(e) => setJdInput(e.target.value)}
                  style={styles.stageTextarea}
                  disabled={isLoading}
                />
                <div style={styles.textareaFooter}>
                  <span>{jdInput.length} characters</span>
                </div>
              </div>

              {/* Right Column: Resume Upload & CTA */}
              <div style={styles.stageRightColumn}>
                <div style={styles.stageSectionHeader}>
                  <h3 style={styles.stageSectionTitle}>Resume Upload</h3>
                  {resumeFile ? (
                    <button onClick={handleClearResume} style={styles.clearBtn}>Clear</button>
                  ) : (
                    <span style={styles.statusBadgeRequired}>Required</span>
                  )}
                </div>

                <div style={styles.uploadDropZone}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.odt,.txt"
                    onChange={handleFileChange}
                    style={styles.fileInput}
                    disabled={isLoading || isPreparingUpload}
                  />
                  <div style={styles.uploadZoneContent}>
                    {isPreparingUpload ? (
                      <div style={styles.uploadLoading}>
                        <div style={styles.spinner}></div>
                        <p>Uploading resume...</p>
                      </div>
                    ) : (
                      <>
                        <div style={styles.uploadIcon}>{fileName ? '📄' : '📁'}</div>
                        <p style={styles.uploadMainText}>
                          {fileName ? fileName : 'Click or drag to select resume'}
                        </p>
                      </>
                    )}
                    <p style={styles.uploadSubText}>PDF, DOCX (Max 5MB)</p>
                  </div>
                </div>

                {/* CTA Action Area */}
                <div style={styles.ctaArea}>
                  {status && (
                    <div style={{
                      ...styles.statusBanner,
                      borderColor: status.startsWith('❌') ? 'var(--color-error)' : 'var(--color-success)',
                      color: status.startsWith('❌') ? 'var(--color-error)' : 'var(--color-success)',
                      backgroundColor: status.startsWith('❌') ? 'var(--color-error-bg)' : 'var(--color-success-bg)',
                    }}>
                      {status}
                    </div>
                  )}

                  {(isLoading || isExtracting || isPreparingUpload) && (
                    <div ref={analysisLoadingRef} style={styles.loadingWrap}>
                      <LoadingStepper phase={loadingPhase} isPreparingUpload={isPreparingUpload} isExtracting={isExtracting} />
                    </div>
                  )}

                  <div style={styles.buttonGroup}>
                    <button
                      onClick={handleAnalyze}
                      style={styles.actionBtnPrimary}
                      disabled={isLoading || isExtracting || isPreparingUpload}
                    >
                      {loadingPhase === 'analyze' ? 'Analyzing...' : 'Start Analysis'}
                    </button>
                    <button
                      onClick={handleReset}
                      style={{
                        ...styles.actionBtnSecondary,
                        opacity: isLoading ? 0.5 : 1,
                        cursor: isLoading ? 'not-allowed' : 'pointer'
                      }}
                      disabled={isLoading}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Tab */}
        {activeTab === 'results' && analysisResults && (
          <div style={styles.resultsContainer}>
            {/* Status Message for Results Tab */}
            {showResultsTopStatus && (
              <div style={styles.statusMessage}>
                {status}
              </div>
            )}
            {/* Loading Animation for Results Tab */}
            {showResultsTopLoading && (
              <LoadingStepper phase={loadingPhase} isPreparingUpload={isPreparingUpload} isExtracting={isExtracting} />
            )}

            <h2 style={styles.resultsHeading}>Analysis Results</h2>

            <div className="results-dashboard-stack">
              {/* New Top Header for Scores — Hidden after optimization */}
              {!optimizationPreview && (
                <div className="results-header-glass">
                  <div style={styles.scoreRingWrapper}>
                    <ScoreRing score={analysisResults.scores?.overall ?? 0} />
                  </div>
                  <div className="results-header-scores">
                    <h3 style={styles.headerScoreTitle}>Match Breakdown</h3>
                    {analysisResults.scores && (
                      <div className="results-header-score-row">
                        {Object.entries(analysisResults.scores).map(([key, value]) => (
                          key !== 'overall' && (
                            <div key={key} style={styles.headerScoreItem}>
                              <span style={styles.headerScoreName}>{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                              <span style={{ ...styles.headerScorePercentage, color: scoreBarColor(value) }}>{value}%</span>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Summary */}
              {analysisResults.ai_summary && (
                <div className="glass-tile dashboard-tile-responsive" style={{ ...styles.summarySection, padding: '32px' }}>
                  <h3 style={styles.mainSectionTitle}>AI Analysis Summary</h3>
                  <p style={styles.summaryText}>{analysisResults.ai_summary}</p>
                </div>
              )}

              {/* Low Score Inline Recommendation Card */}
              {(analysisResults?.scores?.overall != null && analysisResults.scores.overall < 40) && (
                <div className="glass-tile dashboard-tile-responsive" style={{ padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px', background: 'rgba(249, 115, 22, 0.05)', border: '2px solid rgba(249, 115, 22, 0.5)', boxShadow: '0 4px 12px rgba(249, 115, 22, 0.15)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                    <div style={{ fontSize: '24px' }}>💡</div>
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: 'var(--color-text)' }}>Optimization Recommendation</h4>
                      <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: '1.5' }}>
                        Your resume could use a refresh to better match this job. We recommend a quick rewrite for the best results.
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: forcedRewriteMode ? '#f97316' : 'var(--color-text-secondary)' }}>
                      Complete Rewrite
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={forcedRewriteMode}
                      onClick={() => {
                        if (forcedRewriteMode) {
                          setForcedRewriteMode(false);
                          const restoredModes = {};
                          [...workSections, ...projectSections].forEach(sec => {
                            const strategy = sectionStrategies.find(s => s.section_id === sec.sectionId);
                            restoredModes[sec.sectionId] = strategy ? strategy.recommendation : 'surgical';
                          });
                          setSectionModes(restoredModes);
                        } else {
                          const newModes = {};
                          workSections.forEach(s => newModes[s.sectionId] = 'rewrite');
                          projectSections.forEach(s => newModes[s.sectionId] = 'rewrite');
                          setSectionModes(newModes);
                          setForcedRewriteMode(true);
                        }
                      }}
                      style={{
                        width: '44px',
                        height: '24px',
                        backgroundColor: forcedRewriteMode ? '#f97316' : '#e2e8f0',
                        borderRadius: '12px',
                        border: 'none',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        padding: 0
                      }}
                    >
                      <span style={{
                        position: 'absolute',
                        top: '2px',
                        left: forcedRewriteMode ? '22px' : '2px',
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                        transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }} />
                    </button>
                  </div>
                </div>
              )}
              {/* Missing skills after analysis Tracker Bar */}
              {improvementItems.length > 0 ? (
                <div className="hi-tracker">
                  <div className="hi-tracker-icon">🎯</div>
                  <div className="hi-tracker-label">
                    <strong>Missing skills after analysis</strong>
                    {improvementItems.filter(item => item.skill).length > 0 ? (
                      <span>Check skills inside your roles below to inject them</span>
                    ) : (
                      <span>Review recommendations below</span>
                    )}
                  </div>
                  {improvementItems.filter(item => item.skill).length > 0 && (
                    <div className="hi-tracker-pills">
                      {improvementItems.filter(item => item.skill).map(item => {
                        const isSelected = Object.keys(skillEvidence).some(key => key.endsWith("::" + item.skill));
                        return (
                          <span key={item.id} className={`hi-skill-pill${isSelected ? ' active' : ''}`}>
                            {isSelected ? '✓' : '+'} {item.skill}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {improvementItems.filter(item => !item.skill).length > 0 && (
                    <div className="hi-tracker-notices">
                      {improvementItems.filter(item => !item.skill).map(item => (
                        <div key={item.id} className="hi-tracker-notice">{item.text}</div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="hi-tracker-perfect">
                  <div className="hi-tracker-perfect-icon">🎉</div>
                  <div>
                    <strong>Perfect Match!</strong>
                    <span>Your resume already covers all required and preferred skills in the job description.</span>
                  </div>
                </div>
              )}

              {/* Structured Experience Sections with Hybrid Intelligence */}
              {experienceSections.length > 0 && (
                <div ref={experienceSectionRef}>
                  {/* Live-Diff Preview Box (Moved to Tour Modal) */}

                  <div className="hi-unidirectional-container">
                      {/* Section A: Work Experience Optimizations */}
                      {workSections.length > 0 && (
                        <div className="glass-tile dashboard-tile-responsive" style={{ ...styles.experienceSectionsSection, padding: '32px', marginBottom: '0' }}>
                          <div className="hi-section-header-bar" style={{ marginBottom: '24px' }}>
                            <h3 style={{ ...styles.mainSectionTitle, margin: 0 }}>Enhance your Experience skills according to JD</h3>
                            {sectionStrategies.length > 0 && (
                              <div className="hi-section-badge">
                                <span className="badge-dot" />
                                Experience Optimization Active
                              </div>
                            )}
                          </div>
                          <div style={styles.experienceSectionsList}>
                            {workSections.map((section) => {
                              const strategyData = sectionStrategies.find(s => s.section_id === section.sectionId);
                              const currentMode = sectionModes[section.sectionId] || strategyData?.recommendation || 'surgical';


                              let assessmentReason = strategyData?.reason;
                              if (!assessmentReason) {
                                if (isLoadingStrategies) {
                                  assessmentReason = "Evaluating section alignment and keywords against the target job description...";
                                } else if (currentMode === 'no_change') {
                                  assessmentReason = "This section aligns perfectly with the target role. No modifications or skill injections are recommended.";
                                } else if (currentMode === 'surgical') {
                                  assessmentReason = "This section has a solid foundation. AI recommends injecting missing job-description skills surgically.";
                                } else if (currentMode === 'rewrite') {
                                  assessmentReason = "This section requires a deeper rewrite. AI recommends restructuring the bullets to better emphasize core competencies and matching keywords.";
                                }
                              }

                              const strategyMissingSkills = strategyData?.missing_skills || [];
                              const skillsList = improvementItems.filter(item => {
                                if (!item.skill) return false;
                                
                                if (strategyMissingSkills.length > 0) {
                                  return strategyMissingSkills.some(ms => 
                                    ms.toLowerCase() === item.skill.toLowerCase()
                                  );
                                }
                                
                                const validSectionIds = experienceSections.map(s => s.sectionId);
                                const activeRelevantSections = (item.relevantSections || []).filter(sid => validSectionIds.includes(sid));
                                if (activeRelevantSections.length === 0) return true;
                                return activeRelevantSections.includes(section.sectionId);
                              });

                              const selectedCount = skillsList.filter(item =>
                                Object.prototype.hasOwnProperty.call(skillEvidence, `${section.sectionId}::${item.skill}`)
                              ).length;
                              const isSkillsExpanded = expandedSkillSections[section.sectionId] || false;

                              return (
                                <div key={section.sectionId} className={`hi-experience-card mode-${currentMode}`} style={{ animationDelay: `${0.05 * workSections.indexOf(section)}s` }}>
                                  <div className="hi-card-body">
                                    {/* Top bar: Title + Mode Control */}
                                    <div className="hi-card-top-bar">
                                      <div className="hi-card-title-block">
                                        <h4 className="hi-card-role-title">{section.title}</h4>
                                        <div className="hi-card-role-meta">
                                          <span className="hi-company-name">{section.company}</span>
                                          {section.dateRange && (
                                            <>
                                              <span style={{ margin: '0 8px', color: '#cbd5e1' }}>·</span>
                                              <span>{section.dateRange}</span>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                      <div className="hi-seg-control" onClick={e => e.stopPropagation()}>
                                        {[
                                          { id: 'no_change', label: '✨ No Change', cls: 'btn-no-change' },
                                          { id: 'surgical', label: '🔧 Surgical', cls: 'btn-surgical' },
                                          { id: 'rewrite', label: '✏️ Rewrite', cls: 'btn-rewrite' },
                                        ].map(mode => (
                                          <span
                                            key={mode.id}
                                            title={(forcedRewriteMode && mode.id !== 'rewrite') ? "\n\nComplete Rewrite active: Select target skills below to continue, or toggle off Complete Rewrite above to unlock this option." : ""}
                                            style={{ display: 'inline-block', cursor: (forcedRewriteMode && mode.id !== 'rewrite') ? 'not-allowed' : 'pointer' }}
                                          >
                                            <button
                                              type="button"
                                              className={`hi-seg-btn ${mode.cls}${currentMode === mode.id ? ' active' : ''}`}
                                              disabled={isOptimizing || (forcedRewriteMode && mode.id !== 'rewrite')}
                                              onClick={() => setSectionModes(prev => ({ ...prev, [section.sectionId]: mode.id }))}
                                              style={{ pointerEvents: (forcedRewriteMode && mode.id !== 'rewrite') ? 'none' : 'auto' }}
                                            >
                                              {mode.label}
                                            </button>
                                          </span>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Compact AI Insight */}
                                    <div className={`hi-ai-insight mode-${currentMode}`}>
                                      <span className="hi-ai-insight-icon"></span>
                                      <span className="hi-ai-insight-text">{assessmentReason}</span>
                                    </div>

                                    {/* Bullet list */}
                                    {section.bullets.length > 0 && (
                                      <ul className="hi-bullet-list">
                                        {section.bullets.map((bullet, idx) => (
                                          <li key={`${section.sectionId}-${idx}`} className="hi-bullet-item">
                                            {bullet}
                                          </li>
                                        ))}
                                      </ul>
                                    )}

                                    {/* Skills Section */}
                                    {currentMode !== 'no_change' ? (
                                      <>
                                        {skillsList.length === 0 ? (
                                          <div className="hi-fully-optimized" style={{ marginTop: '20px' }}>
                                            <span>🎉</span>
                                            <span>No missing skills mapped to this role — you're fully optimized!</span>
                                          </div>
                                        ) : (
                                          <>
                                            {/* Toggle bar */}
                                            <div
                                              className={`hi-skills-toggle${isSkillsExpanded ? ' expanded' : ''}`}
                                              onClick={() => setExpandedSkillSections(prev => ({ ...prev, [section.sectionId]: !prev[section.sectionId] }))}
                                            >
                                              <div className="hi-skills-toggle-left">
                                                <span>⚙️</span>
                                                <span>Add Missing Skills</span>
                                                <span className="hi-skills-toggle-count">{selectedCount}/{skillsList.length}</span>
                                              </div>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <span className="hi-skills-toggle-hint">
                                                  {isSkillsExpanded ? 'Collapse' : 'Click to configure'}
                                                </span>
                                                <span className="hi-skills-toggle-chevron">▼</span>
                                              </div>
                                            </div>

                                            {/* Expandable skills panel */}
                                            <div className={`hi-skills-panel${isSkillsExpanded ? ' open' : ''}`}>
                                              {skillsList.map(item => {
                                                const isAssignedHere = Object.prototype.hasOwnProperty.call(skillEvidence, `${section.sectionId}::${item.skill}`);
                                                const elsewhereRoles = Object.keys(skillEvidence)
                                                  .filter(key => key.endsWith("::" + item.skill) && !key.startsWith(`${section.sectionId}::`))
                                                  .map(key => {
                                                    const secId = key.split("::")[0];
                                                    const sec = experienceSections.find(s => s.sectionId === secId);
                                                    return sec ? (sec.company || sec.title || "another role") : "another role";
                                                  });
                                                const isAssignedElsewhere = elsewhereRoles.length > 0;

                                                return (
                                                  <div key={item.id} className={`hi-skill-row${isAssignedHere ? ' checked' : ''}`}>
                                                    <label className="hi-skill-row-header">
                                                      <div className="hi-check-wrap">
                                                        <input
                                                          type="checkbox"
                                                          checked={isAssignedHere}
                                                        disabled={isOptimizing}
                                                          onChange={e => toggleSkillSelection(section.sectionId, item.skill, e.target.checked)}
                                                        />
                                                        <div className="hi-check-visual">
                                                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="9 1 3.5 6.5 1 4" />
                                                          </svg>
                                                        </div>
                                                      </div>
                                                      <span className="hi-skill-row-name">{item.skill}</span>
                                                      {!isAssignedHere && (
                                                        <span className="hi-skill-row-preview">{item.text}</span>
                                                      )}
                                                      {isAssignedElsewhere && (
                                                        <span className="hi-skill-row-elsewhere">
                                                          {isAssignedHere ? 'Also in' : 'In'} {elsewhereRoles.join(', ')}
                                                        </span>
                                                      )}
                                                    </label>

                                                    {/* Expanded detail on check */}
                                                    <div className={`hi-skill-detail${isAssignedHere ? ' open' : ''}`}>
                                                      <div className="hi-skill-suggestion">
                                                        <span className="hi-skill-suggestion-sparkle">✨</span>
                                                        <div>
                                                          <strong>Suggested Bullet:</strong> {item.text}
                                                        </div>
                                                      </div>
                                                      <div className="hi-skill-evidence-hint">
                                                        ✦ Add context or metrics to personalize, or leave blank for AI auto-generation.
                                                      </div>
                                                      <textarea
                                                        className="hi-skill-evidence-input"
                                                        value={skillEvidence[`${section.sectionId}::${item.skill}`] || ''}
                                                        onChange={e => updateSkillExperience(section.sectionId, item.skill, e.target.value)}
                                                        placeholder={`e.g. "Used ${item.skill} to improve throughput by 20%"`}
                                                        disabled={isOptimizing}
                                                      />
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </>
                                        )}
                                      </>
                                    ) : (
                                      <div className="hi-preserved-banner">
                                        <span>✨</span>
                                        <span>This section is set to <strong>No Change</strong> — AI will preserve it exactly as-is.</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Section B: Project Optimizations */}
                      {projectSections.length > 0 && (
                        <div className="glass-tile dashboard-tile-responsive" style={{ ...styles.experienceSectionsSection, padding: '32px' }}>
                          <div className="hi-section-header-bar" style={{ marginBottom: '24px' }}>
                            <h3 style={{ ...styles.mainSectionTitle, margin: 0 }}>Level Up Your Projects</h3>
                            {sectionStrategies.length > 0 && (
                              <div className="hi-section-badge">
                                <span className="badge-dot" />
                                Project Optimization Active
                              </div>
                            )}
                          </div>
                          <div style={styles.experienceSectionsList}>
                            {projectSections.map((section) => {
                              const strategyData = sectionStrategies.find(s => s.section_id === section.sectionId);
                              const currentMode = sectionModes[section.sectionId] || strategyData?.recommendation || 'surgical';


                              let assessmentReason = strategyData?.reason;
                              if (!assessmentReason) {
                                if (isLoadingStrategies) {
                                  assessmentReason = "Evaluating section alignment and keywords against the target job description...";
                                } else if (currentMode === 'no_change') {
                                  assessmentReason = "This section aligns perfectly with the target role. No modifications or skill injections are recommended.";
                                } else if (currentMode === 'surgical') {
                                  assessmentReason = "This section has a solid foundation. AI recommends injecting missing job-description skills surgically.";
                                } else if (currentMode === 'rewrite') {
                                  assessmentReason = "This section requires a deeper rewrite. AI recommends restructuring the bullets to better emphasize core competencies and matching keywords.";
                                }
                              }

                              const strategyMissingSkills = strategyData?.missing_skills || [];
                              const skillsList = improvementItems.filter(item => {
                                if (!item.skill) return false;
                                
                                if (strategyMissingSkills.length > 0) {
                                  return strategyMissingSkills.some(ms => 
                                    ms.toLowerCase() === item.skill.toLowerCase()
                                  );
                                }
                                
                                const validSectionIds = experienceSections.map(s => s.sectionId);
                                const activeRelevantSections = (item.relevantSections || []).filter(sid => validSectionIds.includes(sid));
                                if (activeRelevantSections.length === 0) return true;
                                return activeRelevantSections.includes(section.sectionId);
                              });

                              const selectedCount = skillsList.filter(item =>
                                Object.prototype.hasOwnProperty.call(skillEvidence, `${section.sectionId}::${item.skill}`)
                              ).length;
                              const isSkillsExpanded = expandedSkillSections[section.sectionId] || false;

                              return (
                                <div key={section.sectionId} className={`hi-experience-card mode-${currentMode}`} style={{ animationDelay: `${0.05 * projectSections.indexOf(section)}s` }}>
                                  <div className="hi-card-body">
                                    {/* Top bar: Title + Mode Control */}
                                    <div className="hi-card-top-bar">
                                      <div className="hi-card-title-block">
                                        <h4 className="hi-card-role-title">{section.title}</h4>
                                        <div className="hi-card-role-meta">
                                          <span className="hi-company-name">{section.company}</span>
                                          {section.dateRange && (
                                            <>
                                              <span style={{ margin: '0 8px', color: '#cbd5e1' }}>·</span>
                                              <span>{section.dateRange}</span>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                      <div className="hi-seg-control" onClick={e => e.stopPropagation()}>
                                        {[
                                          { id: 'no_change', label: '✨ No Change', cls: 'btn-no-change' },
                                          { id: 'surgical', label: '🔧 Surgical', cls: 'btn-surgical' },
                                          { id: 'rewrite', label: '✏️ Rewrite', cls: 'btn-rewrite' },
                                        ].map(mode => (
                                          <span
                                            key={mode.id}
                                            title={(forcedRewriteMode && mode.id !== 'rewrite') ? "\n\nComplete Rewrite active: Select target skills below to continue, or toggle off Complete Rewrite above to unlock this option." : ""}
                                            style={{ display: 'inline-block', cursor: (forcedRewriteMode && mode.id !== 'rewrite') ? 'not-allowed' : 'pointer' }}
                                          >
                                            <button
                                              type="button"
                                              className={`hi-seg-btn ${mode.cls}${currentMode === mode.id ? ' active' : ''}`}
                                              disabled={isOptimizing || (forcedRewriteMode && mode.id !== 'rewrite')}
                                              onClick={() => setSectionModes(prev => ({ ...prev, [section.sectionId]: mode.id }))}
                                              style={{ pointerEvents: (forcedRewriteMode && mode.id !== 'rewrite') ? 'none' : 'auto' }}
                                            >
                                              {mode.label}
                                            </button>
                                          </span>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Compact AI Insight */}
                                    <div className={`hi-ai-insight mode-${currentMode}`}>
                                      <span className="hi-ai-insight-icon"></span>
                                      <span className="hi-ai-insight-text">{assessmentReason}</span>
                                    </div>

                                    {/* Bullet list */}
                                    {section.bullets.length > 0 && (
                                      <ul className="hi-bullet-list">
                                        {section.bullets.map((bullet, idx) => (
                                          <li key={`${section.sectionId}-${idx}`} className="hi-bullet-item">
                                            {bullet}
                                          </li>
                                        ))}
                                      </ul>
                                    )}

                                    {/* Skills Section */}
                                    {currentMode !== 'no_change' ? (
                                      <>
                                        {skillsList.length === 0 ? (
                                          <div className="hi-fully-optimized" style={{ marginTop: '20px' }}>
                                            <span>🎉</span>
                                            <span>No missing skills mapped to this project — you're fully optimized!</span>
                                          </div>
                                        ) : (
                                          <>
                                            {/* Toggle bar */}
                                            <div
                                              className={`hi-skills-toggle${isSkillsExpanded ? ' expanded' : ''}`}
                                              onClick={() => setExpandedSkillSections(prev => ({ ...prev, [section.sectionId]: !prev[section.sectionId] }))}
                                            >
                                              <div className="hi-skills-toggle-left">
                                                <span>⚙️</span>
                                                <span>Add Missing Skills</span>
                                                <span className="hi-skills-toggle-count">{selectedCount}/{skillsList.length}</span>
                                              </div>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <span className="hi-skills-toggle-hint">
                                                  {isSkillsExpanded ? 'Collapse' : 'Click to configure'}
                                                </span>
                                                <span className="hi-skills-toggle-chevron">▼</span>
                                              </div>
                                            </div>

                                            {/* Expandable skills panel */}
                                            <div className={`hi-skills-panel${isSkillsExpanded ? ' open' : ''}`}>
                                              {skillsList.map(item => {
                                                const isAssignedHere = Object.prototype.hasOwnProperty.call(skillEvidence, `${section.sectionId}::${item.skill}`);
                                                const elsewhereRoles = Object.keys(skillEvidence)
                                                  .filter(key => key.endsWith("::" + item.skill) && !key.startsWith(`${section.sectionId}::`))
                                                  .map(key => {
                                                    const secId = key.split("::")[0];
                                                    const sec = experienceSections.find(s => s.sectionId === secId);
                                                    return sec ? (sec.company || sec.title || "another role") : "another role";
                                                  });
                                                const isAssignedElsewhere = elsewhereRoles.length > 0;

                                                return (
                                                  <div key={item.id} className={`hi-skill-row${isAssignedHere ? ' checked' : ''}`}>
                                                    <label className="hi-skill-row-header">
                                                      <div className="hi-check-wrap">
                                                        <input
                                                          type="checkbox"
                                                          checked={isAssignedHere}
                                                        disabled={isOptimizing}
                                                          onChange={e => toggleSkillSelection(section.sectionId, item.skill, e.target.checked)}
                                                        />
                                                        <div className="hi-check-visual">
                                                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="9 1 3.5 6.5 1 4" />
                                                          </svg>
                                                        </div>
                                                      </div>
                                                      <span className="hi-skill-row-name">{item.skill}</span>
                                                      {!isAssignedHere && (
                                                        <span className="hi-skill-row-preview">{item.text}</span>
                                                      )}
                                                      {isAssignedElsewhere && (
                                                        <span className="hi-skill-row-elsewhere">
                                                          {isAssignedHere ? 'Also in' : 'In'} {elsewhereRoles.join(', ')}
                                                        </span>
                                                      )}
                                                    </label>

                                                    {/* Expanded detail on check */}
                                                    <div className={`hi-skill-detail${isAssignedHere ? ' open' : ''}`}>
                                                      <div className="hi-skill-suggestion">
                                                        <span className="hi-skill-suggestion-sparkle">✨</span>
                                                        <div>
                                                          <strong>Suggested Bullet:</strong> {item.text}
                                                        </div>
                                                      </div>
                                                      <div className="hi-skill-evidence-hint">
                                                        ✦ Add context or metrics to personalize, or leave blank for AI auto-generation.
                                                      </div>
                                                      <textarea
                                                        className="hi-skill-evidence-input"
                                                        value={skillEvidence[`${section.sectionId}::${item.skill}`] || ''}
                                                        onChange={e => updateSkillExperience(section.sectionId, item.skill, e.target.value)}
                                                        placeholder={`e.g. "Used ${item.skill} to improve throughput by 20%"`}
                                                        disabled={isOptimizing}
                                                      />
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </>
                                        )}
                                      </>
                                    ) : (
                                      <div className="hi-preserved-banner">
                                        <span>✨</span>
                                        <span>This section is set to <strong>No Change</strong> — AI will preserve it exactly as-is.</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
              )}


              {/* Optimization status */}
              {optimizationJob && optimizationJob.status !== 'completed' && (
                <div ref={optimizationStatusRef} className="glass-tile" style={styles.optimizationPanel}>
                  <h3 style={styles.mainSectionTitle}>Optimization Status</h3>
                  {optimizationStatusView && (
                    <div style={styles.optimizationStatusBox}>
                      <div style={styles.optimizationHeroRow}>
                        <div style={styles.optimizationStatusHeader}>
                          <div style={styles.optimizationStatusKicker}>Optimizing your resume...</div>
                          <div style={styles.optimizationStatusTitle}>{optimizationStatusView.title}</div>
                          <p style={styles.optimizationStatusDetail}>{optimizationStatusView.detail}</p>
                          <div style={styles.optimizationStageRow}>
                            {[
                              'Getting ready',
                              'Reviewing plan',
                              'Polishing resume',
                              'Creating files',
                            ].map((label, idx) => {
                              const isDone = optimizationStatusView.stageIndex > idx || optimizationJob.status === 'completed';
                              const isCurrent = optimizationStatusView.stageIndex === idx && optimizationStatusView.isActive;
                              return (
                                <span
                                  key={label}
                                  style={
                                    isCurrent
                                      ? styles.optimizationStageChipCurrent
                                      : (isDone ? styles.optimizationStageChipDone : styles.optimizationStageChip)
                                  }
                                >
                                  <span style={styles.optimizationStageChipLabel}>{label}</span>
                                  {isCurrent && <span style={styles.optimizationStageLiveDot} aria-hidden="true" />}
                                </span>
                              );
                            })}
                          </div>
                        </div>

                        <div
                          style={{
                            ...styles.optimizationOrbit,
                            background: `conic-gradient(var(--color-accent-ferrari) ${optimizationStatusView.progress * 3.6}deg, var(--color-border) 0deg)`,
                          }}
                          aria-hidden="true"
                        >
                          <div style={styles.optimizationOrbitInner}>
                            <span style={styles.optimizationOrbitValue}>{optimizationStatusView.progress}%</span>
                          </div>
                        </div>
                      </div>

                      <div style={styles.optimizationProgressTrack}>
                        <div
                          style={{
                            ...styles.optimizationProgressFill,
                            width: `${Math.max(optimizationStatusView.progress, optimizationStatusView.isActive ? 6 : 0)}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {optimizationJob.status === 'failed' && (
                    <button
                      onClick={() => handleOptimizeResume(optimizationJob.job_id)}
                      style={styles.retryBtn}
                      disabled={isOptimizing}
                    >
                      Retry Optimization
                    </button>
                  )}
                </div>
              )}


              {optimizationPreview && (
                <div ref={previewRef} className="glass-tile dashboard-tile-responsive" style={styles.optimizationPanel}>
                  <h3 style={styles.mainSectionTitle}>Optimized Resume Preview</h3>
                  <div className="resume-paper-preview" style={styles.paperContainer}>
                    {/* ── Preview Header ── */}
                    <div style={styles.previewHeader}>
                      <div style={styles.previewHeaderLeft}>
                        <span style={styles.previewBadge}>✦ Preview</span>
                        <h3 style={styles.previewTitle}>
                          {optimizationPreview.candidate_name || 'Optimized Resume'}
                        </h3>
                        {optimizationPreview.headline && (
                          <p style={styles.previewHeadline}>{optimizationPreview.headline}</p>
                        )}
                        {/* Contact info row */}
                        {(() => {
                          const c = optimizationPreview.contact_info || {};
                          const parts = [c.email, c.phone, c.linkedin, c.github].filter(Boolean);
                          return parts.length > 0 ? (
                            <p style={styles.previewContact}>{parts.join(' · ')}</p>
                          ) : null;
                        })()}
                      </div>
                    </div>

                    {/* ── Summary ── */}
                    {optimizationPreview.summary && (
                      <div style={styles.previewSection}>
                        <div style={styles.previewSectionLabel}>Professional Summary</div>
                        <p style={styles.previewSummaryText}>{optimizationPreview.summary}</p>
                      </div>
                    )}

                    {/* ── Skills ── */}
                    {Array.isArray(optimizationPreview.skills) && optimizationPreview.skills.length > 0 && (
                      <div style={styles.previewSection}>
                        <div style={styles.previewSectionLabel}>Core Skills</div>
                        <div style={styles.previewSkillsWrap}>
                          {optimizationPreview.skills.map((skill, idx) => (
                            <span key={`skill-${idx}`} style={styles.previewSkillPill}>{skill}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ── Experience Sections (full, structured) ── */}
                    {Array.isArray(optimizationPreview.experience_sections) && optimizationPreview.experience_sections.length > 0 && (() => {
                      const normalizeBullet = (value) => String(value || '')
                        .replace(/^[-•*\u2022\u25CF\u25CB\u2023\u2043\s]+/, '')
                        .replace(/\s+/g, ' ')
                        .trim()
                        .toLowerCase();

                      const getSectionKey = (section) => String(
                        section?.section_id || `${section?.title || ''}::${section?.company || ''}`
                      )
                        .trim()
                        .toLowerCase();

                      const getSectionTitleCompanyKey = (section) => String(
                        `${section?.title || ''}::${section?.company || ''}`
                      )
                        .trim()
                        .toLowerCase();

                      const parseTraceSectionIds = (traceItem) => {
                        const ids = new Set();
                        const explicitTarget = String(traceItem?.targetSectionId || '').trim().toLowerCase();
                        if (explicitTarget) ids.add(explicitTarget);

                        (traceItem?.outputLocations || []).forEach((location) => {
                          const loc = String(location || '').trim().toLowerCase();
                          if (loc.startsWith('experience_sections:')) {
                            const id = loc.slice('experience_sections:'.length).trim();
                            if (id) ids.add(id);
                          }
                        });

                        return ids;
                      };

                      const claimedInsertionRules = claimedSkillTrace
                        .filter((item) => item.applied)
                        .map((item) => ({
                          skillLower: String(item.skill || '').trim().toLowerCase(),
                          experienceLower: String(item.experience || '').trim().toLowerCase(),
                          targetSectionIds: parseTraceSectionIds(item),
                          targetSectionLabelLower: String(item.targetSectionLabel || '').trim().toLowerCase(),
                        }))
                        .filter((item) => item.skillLower);

                      const insertedBulletKeysBySection = new Map();

                      (optimizationPreview.experience_sections || []).forEach((section) => {
                        const sectionKey = getSectionKey(section);
                        const originalBullets = Array.isArray(section?.original_bullets)
                          ? section.original_bullets.map((item) => String(item || '').trim()).filter(Boolean)
                          : [];
                        const optimizedBullets = Array.isArray(section?.bullets)
                          ? section.bullets.map((item) => String(item || '').trim()).filter(Boolean)
                          : [];

                        if (!sectionKey || optimizedBullets.length === 0) return;

                        const originalBulletSet = new Set(
                          originalBullets.map((bullet) => normalizeBullet(bullet)).filter(Boolean)
                        );

                        const insertedBulletKeys = new Set(
                          optimizedBullets
                            .filter((bullet) => !originalBulletSet.has(normalizeBullet(bullet)))
                            .map((bullet) => normalizeBullet(bullet))
                            .filter(Boolean)
                        );

                        if (insertedBulletKeys.size > 0) {
                          insertedBulletKeysBySection.set(sectionKey, insertedBulletKeys);
                        }
                      });

                      const isInsertedBullet = (bullet, section) => {
                        const sectionKey = getSectionKey(section);
                        const insertedKeys = insertedBulletKeysBySection.get(sectionKey);
                        if (!insertedKeys) return false;

                        const bulletKey = normalizeBullet(bullet);
                        if (!insertedKeys.has(bulletKey)) return false;

                        const sectionIdLower = String(section?.section_id || '').trim().toLowerCase();
                        const sectionTitleCompany = getSectionTitleCompanyKey(section);
                        const sectionHeader = String(`${section?.title || ''} ${section?.company || ''}`)
                          .trim()
                          .toLowerCase();

                        const bulletMatchesClaimedSkill = (rule) => {
                          // Split skill into significant words (>3 chars) for partial matching.
                          // AI-generated bullets paraphrase the skill rather than quoting it verbatim,
                          // so "Delivering change" → look for "delivering" OR "change" in the bullet.
                          const skillWords = rule.skillLower
                            .split(/\s+/)
                            .filter((w) => w.length > 3);
                          const hasSkillWordMatch = skillWords.length > 0 &&
                            skillWords.some((w) => bulletKey.includes(w));
                          const normExp = normalizeBullet(rule.experienceLower);
                          const hasExperienceMention = normExp.length > 12 && (
                            bulletKey.includes(normExp.slice(0, 64))
                            || normExp.includes(bulletKey.slice(0, 64))
                          );
                          return hasSkillWordMatch || hasExperienceMention;
                        };

                        const sectionMatchesRule = (rule) => {
                          if (rule.targetSectionIds.size > 0) {
                            return rule.targetSectionIds.has(sectionIdLower);
                          }

                          if (rule.targetSectionLabelLower) {
                            return rule.targetSectionLabelLower.includes(sectionTitleCompany)
                              || sectionHeader.includes(rule.targetSectionLabelLower)
                              || rule.targetSectionLabelLower.includes(sectionHeader);
                          }

                          return true;
                        };

                        return claimedInsertionRules.some((rule) => (
                          sectionMatchesRule(rule) && bulletMatchesClaimedSkill(rule)
                        ));
                      };

                      return (
                        <div style={styles.previewSection}>
                          <div style={styles.previewSectionLabel}>Experience</div>
                          <p style={styles.previewSectionHint}>
                            Highlighted bullets mark inserted changes (Surgical mode) or rewritten bullet comparisons (Rewrite mode).
                          </p>
                          {optimizationPreview.experience_sections.map((section) => {
                            const strategyData = sectionStrategies.find(s => s.section_id === section.section_id || s.section_id === section.sectionId);
                            const currentMode = sectionModes[section.section_id] || sectionModes[section.sectionId] || strategyData?.recommendation || 'surgical';
                            const headerBg = currentMode === 'no_change' ? '#f8fafc' : (currentMode === 'surgical' ? '#eef2ff' : '#fff7ed');
                            const headerBorder = currentMode === 'no_change' ? 'rgba(226, 232, 240, 0.8)' : (currentMode === 'surgical' ? '#6366f1' : '#f97316');
                            const headerText = currentMode === 'no_change' ? '#475569' : (currentMode === 'surgical' ? '#3730a3' : '#9a3412');
                            const modeLabel = { no_change: '✨ No Change', surgical: '🔧 Surgical', rewrite: '✏️ Rewrite' }[currentMode] || currentMode;

                            return (
                              <div key={section.section_id || `${section.title}-${section.company}`} style={{ marginBottom: '24px' }}>
                                {/* Mode Header Tab */}
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  background: headerBg,
                                  border: `1.5px solid ${headerBorder}`,
                                  borderBottom: 'none',
                                  borderTopLeftRadius: '12px',
                                  borderTopRightRadius: '12px',
                                  padding: '8px 16px',
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  color: headerText,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  <span>Strategy Applied: {modeLabel}</span>
                                </div>
                                <div style={{
                                  ...styles.previewRoleCard,
                                  marginTop: 0,
                                  borderTopLeftRadius: 0,
                                  borderTopRightRadius: 0,
                                  border: `1.5px solid ${headerBorder}`,
                                  background: currentMode === 'no_change' ? 'var(--color-surface)' : (currentMode === 'surgical' ? '#fafaff' : '#fffcf9'),
                                  padding: '16px 20px',
                                }}>
                                  {/* Role header */}
                                  <div style={styles.previewRoleHeader}>
                                    <div style={styles.previewRoleLeft}>
                                      <span style={styles.previewRoleTitle}>{section.title || 'Role'}</span>
                                      {section.company && (
                                        <span style={styles.previewRoleCompany}> — {section.company}</span>
                                      )}
                                    </div>
                                    {section.date_range && (
                                      <span style={styles.previewRoleDate}>{section.date_range}</span>
                                    )}
                                  </div>
                                  {/* Bullets — all of them, with claimed-skill highlights or rewrite comparison */}
                                  {currentMode === 'rewrite' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '12px' }}>
                                      <div>
                                        <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#dc2626', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          <span>🔴 Previous Bullets</span>
                                        </div>
                                        <ul style={{ ...styles.previewBulletList, opacity: 0.9, listStyleType: 'disc', paddingLeft: '20px' }}>
                                          {(section.original_bullets || []).map((bullet, bIdx) => (
                                            <li key={`orig-${bIdx}`} style={{ ...styles.previewBulletItem, textDecoration: 'none', borderLeft: '3px solid #dc2626', background: 'rgba(220, 38, 38, 0.05)', paddingLeft: '8px', color: '#475569' }}>
                                              {bullet}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                      <div>
                                        <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#16a34a', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          <span>🟢 Rewritten Bullets</span>
                                        </div>
                                        <ul style={{ ...styles.previewBulletList, listStyleType: 'disc', paddingLeft: '20px' }}>
                                          {(section.bullets || []).map((bullet, bIdx) => (
                                            <li key={`rewrit-${bIdx}`} style={{ ...styles.previewBulletItemNew, borderLeft: '3px solid #16a34a', background: 'rgba(22, 163, 74, 0.05)', paddingLeft: '8px' }}>
                                              {bullet}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    </div>
                                  ) : (
                                    Array.isArray(section.bullets) && section.bullets.length > 0 && (
                                      <ul style={styles.previewBulletList}>
                                        {section.bullets.map((bullet, bIdx) => {
                                          const isInserted = isInsertedBullet(bullet, section);
                                          return (
                                            <li
                                              key={`${section.section_id || bIdx}-b-${bIdx}`}
                                              style={isInserted ? styles.previewBulletItemNew : styles.previewBulletItem}
                                            >
                                              {bullet}
                                              {isInserted && <span style={styles.previewNewBadge}>INSERTED</span>}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    )
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* Fallback: flat bullets if no structured sections */}
                    {(!Array.isArray(optimizationPreview.experience_sections) || optimizationPreview.experience_sections.length === 0) &&
                      Array.isArray(optimizationPreview.experience_bullets) && optimizationPreview.experience_bullets.length > 0 && (
                        <div style={styles.previewSection}>
                          <div style={styles.previewSectionLabel}>Experience Highlights</div>
                          <ul style={styles.previewBulletList}>
                            {optimizationPreview.experience_bullets.map((bullet, idx) => (
                              <li key={`fb-${idx}`} style={styles.previewBulletItem}>{String(bullet || '')}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                    {/* ── Education ── */}
                    {Array.isArray(optimizationPreview.education) && optimizationPreview.education.length > 0 && (
                      <div style={styles.previewSection}>
                        <div style={styles.previewSectionLabel}>Education</div>
                        {optimizationPreview.education.map((item, idx) => (
                          <div key={`edu-${idx}`} style={styles.previewEduRow}>
                            <div style={styles.previewEduLeft}>
                              <span style={styles.previewEduDegree}>{item.degree}</span>
                              {item.institution && <span style={styles.previewEduInst}> — {item.institution}</span>}
                            </div>
                            {item.date_range && <span style={styles.previewRoleDate}>{item.date_range}</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ── Claimed Skills Audit Trail ── */}
                    {claimedSkillTrace.length > 0 && (
                      <div style={styles.previewSection}>
                        <div style={styles.previewSectionLabel}>Skills Applied ✦</div>
                        {claimedSkillTrace.map((item) => (
                          <div key={item.skill} style={styles.previewTraceItem} className="rf-preview-trace-item">
                            <div style={styles.previewTraceHeader}>
                              <span style={styles.previewTraceSkillName}>{item.skill}</span>
                              <span style={item.applied ? styles.previewTraceApplied : styles.previewTraceNotApplied}>
                                {item.applied ? '✓ Applied' : '✗ Not Applied'}
                              </span>
                            </div>
                            {item.reason && (
                              <p style={styles.previewTraceReason}>{item.reason}</p>
                            )}
                            {(item.targetSectionLabel || item.targetSectionId) && (
                              <p style={styles.previewTraceReason}>
                                Injected into: <strong>{item.targetSectionLabel || item.targetSectionId}</strong>
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Score Comparison Card */}
              {scoreComparison && scoreComparison.pre_optimization_score != null && scoreComparison.post_optimization_score != null && (
                <div className="glass-tile" style={{
                  ...styles.optimizationPanel,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(240,253,250,0.95) 50%, rgba(243,244,246,0.92) 100%)',
                  border: '1.5px solid rgba(45, 90, 61, 0.25)',
                  boxShadow: '0 12px 30px -10px rgba(45, 90, 61, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                  marginTop: '2rem',
                  marginBottom: '1.5rem',
                  borderRadius: '16px',
                  padding: '24px',
                }}>
                  {/* Header Row */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                    <div>
                      <h3 style={{ ...styles.mainSectionTitle, margin: 0, fontSize: '1.05rem', color: 'var(--valley-deep-jade)', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700 }}>
                        <span>📈</span> Optimization Impact
                      </h3>
                      <p style={{ margin: '2px 0 0 0', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                        Compare initial vs. optimized resume alignment
                      </p>
                    </div>
                    <button
                      onClick={() => setShowScoreBreakdown(!showScoreBreakdown)}
                      style={{
                        background: 'rgba(45, 90, 61, 0.08)',
                        border: '1px solid rgba(45, 90, 61, 0.2)',
                        color: 'var(--valley-jade)',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: 'var(--font-heading)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(45, 90, 61, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(45, 90, 61, 0.08)';
                      }}
                    >
                      {showScoreBreakdown ? 'Hide Breakdown ▴' : 'Show Breakdown ▾'}
                    </button>
                  </div>

                  {/* Headline Comparison Dashboard */}
                  {(() => {
                    const formatScore = (num) => {
                      if (num == null) return '';
                      const val = Number(num);
                      return val % 1 !== 0 ? val.toFixed(1) : val.toString();
                    };
                    const preScoreStr = formatScore(scoreComparison.pre_optimization_score);
                    const postScoreStr = formatScore(scoreComparison.post_optimization_score);
                    const preHasDecimal = preScoreStr.includes('.');
                    const postHasDecimal = postScoreStr.includes('.');
                    const boostStr = formatScore(scoreComparison.post_optimization_score - scoreComparison.pre_optimization_score);

                    return (
                      <div className="score-comparison-ring-row" style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-around',
                        background: 'rgba(255, 255, 255, 0.6)',
                        borderRadius: '12px',
                        padding: '20px',
                        border: '1px solid rgba(226, 232, 240, 0.8)',
                        marginBottom: '1.25rem',
                        gap: '1rem',
                      }}>
                        {/* Before Circle */}
                        <div style={{ textAlign: 'center', flex: 1 }}>
                          <div style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)', fontFamily: 'var(--font-heading)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: '6px' }}>Initial Match</div>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            border: '3px solid var(--color-charcoal-200)',
                            backgroundColor: 'var(--color-surface)',
                            color: 'var(--color-text-secondary)',
                            fontSize: preHasDecimal ? '1.25rem' : '1.6rem',
                            fontWeight: 800,
                            fontFamily: 'var(--font-heading)',
                            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.03)',
                          }}>
                            {preScoreStr}%
                          </div>
                        </div>

                        {/* Transition Indicator */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1.5, gap: '8px' }}>
                          {scoreComparison.post_optimization_score > scoreComparison.pre_optimization_score && (
                            <div style={{
                              background: 'linear-gradient(135deg, var(--valley-emerald) 0%, var(--valley-aqua) 100%)',
                              color: '#fff',
                              padding: '4px 14px',
                              borderRadius: '30px',
                              fontSize: '0.78rem',
                              fontWeight: 800,
                              fontFamily: 'var(--font-heading)',
                              boxShadow: '0 4px 12px rgba(61, 139, 94, 0.25)',
                              letterSpacing: '0.02em',
                              animation: 'pulse 2s infinite',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}>
                              <span>✦</span> +{boostStr}% Match Boost
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '6px' }}>
                            <div style={{ height: '3px', background: 'var(--color-charcoal-200)', flex: 1, borderRadius: '2px' }}></div>
                            <span className="score-comparison-arrow" style={{ fontSize: '1.25rem', color: 'var(--valley-emerald)', animation: 'bounceX 1.5s infinite' }}>➔</span>
                            <div style={{ height: '3px', background: 'var(--valley-emerald)', flex: 1, borderRadius: '2px' }}></div>
                          </div>
                        </div>

                        {/* After Circle */}
                        <div style={{ textAlign: 'center', flex: 1 }}>
                          <div style={{ fontSize: '0.65rem', color: 'var(--valley-jade)', fontFamily: 'var(--font-heading)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 800, marginBottom: '6px' }}>Optimized Match</div>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '85px',
                            height: '85px',
                            borderRadius: '50%',
                            border: '3px solid var(--valley-emerald)',
                            backgroundColor: 'var(--color-surface)',
                            color: 'var(--valley-emerald)',
                            fontSize: postHasDecimal ? '1.35rem' : '1.8rem',
                            fontWeight: 800,
                            fontFamily: 'var(--font-heading)',
                            boxShadow: '0 0 16px rgba(61, 139, 94, 0.2), inset 0 2px 4px rgba(61,139,94,0.05)',
                            position: 'relative',
                          }}>
                            <span style={{ position: 'absolute', top: '-6px', right: '-6px', fontSize: '0.9rem' }}>✨</span>
                            {postScoreStr}%
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Component Breakdown List (Expandable) */}
                  {showScoreBreakdown && scoreComparison.pre_breakdown && scoreComparison.post_breakdown && (
                    <div className="score-comparison-grid" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: '1rem',
                      paddingTop: '1.25rem',
                      borderTop: '1px solid rgba(226, 232, 240, 0.9)',
                      animation: 'fadeIn 0.3s ease-out',
                    }}>
                      {[
                        { key: 'skill_match', label: 'Skill Match', icon: '🛠' },
                        { key: 'experience_match', label: 'Experience Match', icon: '💼' },
                        { key: 'education_match', label: 'Education Match', icon: '🎓' },
                        { key: 'cultural_fit', label: 'Cultural Fit', icon: '🤝' },
                      ].map(({ key, label, icon }) => {
                        const pre = scoreComparison.pre_breakdown[key] ?? 0;
                        const post = scoreComparison.post_breakdown[key] ?? 0;
                        const delta = post - pre;
                        return (
                          <div key={key} style={{
                            background: 'var(--color-surface)',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                            borderRadius: '10px',
                            padding: '12px 14px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.01)',
                          }}>
                            {/* Metric Header */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                              <span style={{ fontSize: '0.78rem', fontWeight: 650, color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ opacity: 0.85 }}>{icon}</span> {label}
                              </span>
                              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--color-text-secondary)' }}>
                                {pre}% → <strong style={{ color: 'var(--valley-emerald)' }}>{post}%</strong>
                              </span>
                            </div>

                            {/* Dual-layered Comparative Progress Track */}
                            <div style={{
                              position: 'relative',
                              height: '8px',
                              width: '100%',
                              backgroundColor: 'rgba(15, 23, 42, 0.06)',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              marginBottom: '6px',
                            }}>
                              {/* Baseline Score Bar */}
                              <div style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                height: '100%',
                                width: `${pre}%`,
                                backgroundColor: 'var(--color-charcoal-400)',
                                borderRadius: '4px 0 0 4px',
                                transition: 'width 1s ease-out',
                              }}></div>
                              {/* Improvement Highlight Bar */}
                              {delta > 0 && (
                                <div style={{
                                  position: 'absolute',
                                  left: `${pre}%`,
                                  top: 0,
                                  height: '100%',
                                  width: `${delta}%`,
                                  background: 'linear-gradient(to right, var(--color-accent-ferrari), var(--valley-emerald))',
                                  borderRadius: pre === 0 ? '4px' : '0 4px 4px 0',
                                  boxShadow: '0 0 4px rgba(61, 139, 94, 0.3)',
                                  transition: 'width 1s ease-out 0.3s',
                                }}></div>
                              )}
                            </div>

                            {/* Info Footer */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', height: '14px' }}>
                              {delta > 0 ? (
                                <span style={{ fontSize: '0.68rem', color: 'var(--valley-emerald)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '2px' }}>
                                  ▲ +{delta}% Improvement
                                </span>
                              ) : (
                                <span style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)', fontWeight: 500 }}>
                                  Fully aligned
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sticky Footer */}
            <div className={`results-sticky-footer${isFooterVisible ? ' visible' : ''}`}>
              {/* Credit Indicator — show near the CTA so the user knows before clicking */}
              {paymentStatus && !paymentStatus.is_subscribed && (
                <div
                  id="credit-indicator-chip"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 14px',
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.78rem',
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    whiteSpace: 'nowrap',
                    border: '1.5px solid',
                    transition: 'all var(--transition-fast)',
                    cursor: paymentStatus.credits === 0 ? 'pointer' : 'default',
                    ...(paymentStatus.credits === 0
                      ? {
                        background: 'var(--color-warning-bg)',
                        borderColor: 'var(--color-warning-border)',
                        color: 'var(--color-warning)',
                      }
                      : {
                        background: 'var(--color-success-bg)',
                        borderColor: 'var(--color-success-border)',
                        color: 'var(--color-success)',
                      }),
                  }}
                  onClick={() => { if (paymentStatus.credits === 0) setIsUpgradeModalOpen(true); }}
                  title={paymentStatus.credits === 0 ? 'Click to purchase credits' : `${paymentStatus.credits} optimization credit${paymentStatus.credits !== 1 ? 's' : ''} remaining`}
                >
                  <span style={{ fontSize: '14px', lineHeight: 1 }} aria-hidden="true">
                    {paymentStatus.credits === 0 ? '⚠' : '⬡'}
                  </span>
                  {paymentStatus.credits === 0
                    ? 'No credits — upgrade'
                    : `${paymentStatus.credits} credit${paymentStatus.credits !== 1 ? 's' : ''}`}
                </div>
              )}
              {paymentStatus?.is_subscribed && (
                <div
                  id="credit-indicator-chip"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 14px',
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.78rem',
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    whiteSpace: 'nowrap',
                    border: '1.5px solid',
                    background: 'var(--color-accent-glow)',
                    borderColor: 'rgba(79, 70, 229, 0.2)',
                    color: 'var(--color-accent-ferrari)',
                  }}
                >
                  <span style={{ fontSize: '14px', lineHeight: 1 }} aria-hidden="true">✦</span>
                  Pro
                </div>
              )}
              <button
                onClick={() => handleOptimizeResume()}
                style={{ ...styles.optimizeBtn, opacity: isOptimizing ? 0.7 : 1, width: 'auto' }}
                disabled={isOptimizing}
              >
                {isOptimizing ? 'Optimizing...' : 'Optimize Resume'}
              </button>

              <button
                onClick={handleReset}
                style={{
                  ...styles.newAnalysisBtn,
                  width: 'auto',
                  opacity: isOptimizing ? 0.5 : 1,
                  cursor: isOptimizing ? 'not-allowed' : 'pointer',
                }}
                disabled={isOptimizing}
              >
                New Analysis
              </button>

              {visibleArtifacts.length > 0 && (
                <div ref={downloadBtnRef} className={`sticky-download-group ${tourStep === 1 ? 'tour-highlight-pulse' : ''}`.trim()} style={{ display: 'flex', gap: '12px', borderLeft: '1px solid var(--color-border)', paddingLeft: '16px' }}>
                  {visibleArtifacts.map((artifact) => (
                    <a
                      key={artifact.artifact_id}
                      href={toAbsoluteDownloadUrl(artifact.download_url)}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.stickyDownloadBtn}
                      title={`Download ${artifact.file_name}`}
                      aria-label={`Download ${artifact.file_name}`}
                    >
                      {String(artifact.kind || '').toLowerCase() === 'docx' ? 'Download as Docx' : String(artifact.kind || '').toUpperCase()}
                    </a>
                  ))}

                  {/* On-Demand PDF Download Button */}
                  <button
                    id="btn-download-pdf"
                    onClick={() => handleDownloadPdf(optimizationJob.job_id)}
                    disabled={isDownloadingPdf}
                    style={{
                      ...styles.stickyDownloadBtn,
                      opacity: isDownloadingPdf ? 0.75 : 1,
                      cursor: isDownloadingPdf ? 'not-allowed' : 'pointer',
                      minWidth: '148px',
                    }}
                    title={isDownloadingPdf ? 'Converting DOCX to PDF...' : hasDownloadedPdf ? 'Download PDF again' : 'Download PDF'}
                    aria-label={isDownloadingPdf ? 'Converting PDF...' : 'Download PDF'}
                  >
                    {isDownloadingPdf ? (
                      <>
                        <span style={styles.pdfBtnSpinner} />
                        &nbsp;Converting…
                      </>
                    ) : hasDownloadedPdf ? (
                      'Download PDF ✓'
                    ) : (
                      'Download as PDF'
                    )}
                  </button>

                  {/* Save to Tracker Button */}
                  {optimizationJob && optimizationJob.status === 'completed' && (
                    <div style={{ 
                      position: 'relative', 
                      display: 'flex', 
                      alignItems: 'center', 
                      borderLeft: '1px solid var(--color-border)', 
                      paddingLeft: '16px',
                      marginLeft: '4px' 
                    }}>
                      <button
                        ref={saveTrackerBtnRef}
                        onClick={handleSaveToTracker}
                        className={tourStep === 2 ? 'tour-highlight-pulse' : ''}
                        style={{
                          ...styles.stickyDownloadBtn,
                          backgroundColor: savedToTracker ? '#2e7d32' : 'var(--color-accent-ferrari)',
                          color: '#ffffff',
                          border: 'none',
                          opacity: isSavingToTracker ? 0.75 : 1,
                          cursor: isSavingToTracker || (savedToTracker && tourStep !== 2) ? 'not-allowed' : 'pointer',
                        }}
                        disabled={isSavingToTracker || (savedToTracker && tourStep !== 2)}
                      >
                        {isSavingToTracker ? 'Saving...' : (savedToTracker ? 'Saved!' : 'Save to Tracker')}
                      </button>
                      {showConfetti && (
                        <div className="tour-confetti-burst">
                          {[...Array(12)].map((_, i) => (
                            <div key={i} className={`confetti-piece p${i}`} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <HistoryTable />
        )}
      </div>

      {/* --- Modals --- */}
      {isAccountDetailsModalOpen && (
        <div style={modalStyles.overlay} onClick={() => { setIsAccountDetailsModalOpen(false); setShowCancelConfirm(false); }}>
          <div className="responsive-modal" style={modalStyles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={modalStyles.modalHeader}>
              <h3 style={modalStyles.modalTitle}>Account Details</h3>
              <button style={modalStyles.modalCloseBtn} onClick={() => { setIsAccountDetailsModalOpen(false); setShowCancelConfirm(false); }}>✕</button>
            </div>
            <div style={modalStyles.modalBody}>
              <div style={modalStyles.accountRow}>
                <span style={modalStyles.accountLabel}>First Name</span>
                <span style={modalStyles.accountValue}>{user?.firstName || '-'}</span>
              </div>
              <div style={modalStyles.accountRow}>
                <span style={modalStyles.accountLabel}>Last Name</span>
                <span style={modalStyles.accountValue}>{user?.lastName || '-'}</span>
              </div>
              <div style={modalStyles.accountRow}>
                <span style={modalStyles.accountLabel}>Email</span>
                <span style={modalStyles.accountValue}>{user?.email || '-'}</span>
              </div>
              <div style={modalStyles.accountRow}>
                <span style={modalStyles.accountLabel}>Login Method</span>
                <span style={modalStyles.accountValueBadge}>
                  {user?.auth_provider === 'google' ? 'Google SSO' : 'Email/Password'}
                </span>
              </div>

              {/* Subscription Management Section */}
              {paymentStatus?.is_subscribed && (
                <>
                  <div style={{
                    borderTop: '1px solid var(--color-border)',
                    paddingTop: '16px',
                    marginTop: '4px',
                  }}>
                    <div style={modalStyles.accountRow}>
                      <span style={modalStyles.accountLabel}>Plan</span>
                      <span style={{
                        ...modalStyles.accountValueBadge,
                        backgroundColor: 'rgba(79, 70, 229, 0.08)',
                        color: 'var(--color-accent-ferrari)',
                      }}>
                        ✦ Unlimited Monthly
                      </span>
                    </div>

                    {/* Canceling state: show access-until date + reactivate button */}
                    {paymentStatus.cancel_at_period_end ? (
                      <div style={{
                        background: 'var(--color-warning-bg)',
                        border: '1px solid var(--color-warning-border)',
                        borderRadius: 'var(--radius-md)',
                        padding: '14px 16px',
                        marginTop: '8px',
                      }}>
                        <p style={{
                          fontSize: '0.85rem',
                          color: 'var(--color-warning)',
                          fontWeight: 600,
                          margin: '0 0 4px 0',
                        }}>
                          ⚠ Subscription canceling
                        </p>
                        <p style={{
                          fontSize: '0.8rem',
                          color: 'var(--color-text-secondary)',
                          margin: '0 0 12px 0',
                          lineHeight: 1.5,
                        }}>
                          You'll keep unlimited access until{' '}
                          <strong>
                            {paymentStatus.current_period_end
                              ? new Date(paymentStatus.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                              : 'your billing period ends'}
                          </strong>.
                          After that, your plan will switch to pay-per-use credits.
                        </p>
                        <button
                          id="btn-reactivate-subscription"
                          onClick={handleReactivateSubscription}
                          disabled={isReactivatingSub}
                          style={{
                            background: 'linear-gradient(135deg, var(--color-accent-ferrari), var(--color-purple-600))',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            padding: '10px 20px',
                            fontFamily: 'var(--font-heading)',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            cursor: isReactivatingSub ? 'not-allowed' : 'pointer',
                            opacity: isReactivatingSub ? 0.7 : 1,
                            transition: 'all var(--transition-fast)',
                            width: '100%',
                          }}
                        >
                          {isReactivatingSub ? 'Reactivating...' : 'Keep My Subscription'}
                        </button>
                      </div>
                    ) : (
                      /* Active state: show cancel option */
                      <div style={{ marginTop: '8px' }}>
                        {!showCancelConfirm ? (
                          <button
                            id="btn-show-cancel-confirm"
                            onClick={() => setShowCancelConfirm(true)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--color-text-muted)',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              cursor: 'pointer',
                              padding: '4px 0',
                              textDecoration: 'underline',
                              textUnderlineOffset: '3px',
                              transition: 'color var(--transition-fast)',
                            }}
                          >
                            Cancel subscription
                          </button>
                        ) : (
                          <div style={{
                            background: 'var(--color-error-bg)',
                            border: '1px solid var(--color-error-border)',
                            borderRadius: 'var(--radius-md)',
                            padding: '14px 16px',
                            animation: 'rz-fadeIn 0.2s ease',
                          }}>
                            <p style={{
                              fontSize: '0.85rem',
                              color: 'var(--color-text-primary)',
                              fontWeight: 600,
                              margin: '0 0 4px 0',
                            }}>
                              Cancel your subscription?
                            </p>
                            <p style={{
                              fontSize: '0.8rem',
                              color: 'var(--color-text-secondary)',
                              margin: '0 0 12px 0',
                              lineHeight: 1.5,
                            }}>
                              You'll still have unlimited access until the end of your current billing period. After that, you'll switch to pay-per-use credits.
                            </p>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                id="btn-confirm-cancel-subscription"
                                onClick={handleCancelSubscription}
                                disabled={isCancelingSub}
                                style={{
                                  flex: 1,
                                  background: 'var(--color-error)',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: 'var(--radius-sm)',
                                  padding: '9px 16px',
                                  fontFamily: 'var(--font-heading)',
                                  fontWeight: 700,
                                  fontSize: '0.82rem',
                                  cursor: isCancelingSub ? 'not-allowed' : 'pointer',
                                  opacity: isCancelingSub ? 0.7 : 1,
                                  transition: 'all var(--transition-fast)',
                                }}
                              >
                                {isCancelingSub ? 'Canceling...' : 'Yes, Cancel'}
                              </button>
                              <button
                                id="btn-dismiss-cancel-confirm"
                                onClick={() => setShowCancelConfirm(false)}
                                style={{
                                  flex: 1,
                                  background: 'var(--color-surface)',
                                  color: 'var(--color-text-secondary)',
                                  border: '1px solid var(--color-border)',
                                  borderRadius: 'var(--radius-sm)',
                                  padding: '9px 16px',
                                  fontFamily: 'var(--font-heading)',
                                  fontWeight: 600,
                                  fontSize: '0.82rem',
                                  cursor: 'pointer',
                                  transition: 'all var(--transition-fast)',
                                }}
                              >
                                Never mind
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full-screen Glassmorphic Overlay for Processing/Optimization */}
      {(isLoading || isExtracting || isPreparingUpload || isOptimizing) && (
        <div style={styles.overlayBackdrop}>
          <div style={styles.overlayCard} className="animate-greeting">
            {isOptimizing && optimizationSuccess ? (
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 0' }} className="animate-greeting">
                <div className="success-checkmark-circle">
                  <div className="success-checkmark-kick"></div>
                </div>
                <h3 style={{ ...styles.overlayStatusTitle, marginTop: '24px', color: 'var(--color-success)', fontSize: '22px', fontWeight: 800 }}>
                  Tailored Resume Package Ready!
                </h3>
                <p style={{ ...styles.overlayStatusDetail, maxWidth: '440px', margin: '10px auto 0', fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                  We've successfully updated your highlight bullets and aligned your skills. Preparing your downloadable results...
                </p>
              </div>
            ) : isOptimizing && optimizationStatusView ? (
              <div style={{ width: '100%' }}>
                <div style={styles.overlayHeader}>
                  <span style={styles.overlayStatusKicker}>⚡ Optimizing your resume...</span>
                  <h3 style={styles.overlayStatusTitle}>{optimizationStatusView.title}</h3>
                  <p style={styles.overlayStatusDetail}>{optimizationStatusView.detail}</p>
                </div>

                <div style={styles.overlayStepperWrap}>
                  <OptimizationStepper job={optimizationJob} />
                </div>
              </div>
            ) : (
              <div style={styles.overlayStepperWrap}>
                <LoadingStepper phase={loadingPhase} isPreparingUpload={isPreparingUpload} isExtracting={isExtracting} />
              </div>
            )}

            {/* Curated Career Insight Card */}
            {!isPreparingUpload && loadingPhase !== 'upload' && !optimizationSuccess && (shuffledFacts.length > 0 ? shuffledFacts : combinedFacts)[currentFactIndex] && (() => {
              const displayFacts = shuffledFacts.length > 0 ? shuffledFacts : combinedFacts;
              const currentFact = displayFacts[currentFactIndex];
              return (
                <div style={styles.overlayFactCard} className="animate-greeting" key={currentFactIndex}>
                  <div style={styles.overlayFactHeader}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '6px' }}>💡</span>
                      <span style={styles.overlayFactTitle}>{currentFact.title}</span>
                    </div>
                  </div>
                  <p style={styles.overlayFactText}>
                    <style>{`
                      @keyframes fadeInChar {
                        to {
                          opacity: 1;
                          transform: translateY(0);
                        }
                      }
                    `}</style>
                    "
                    {currentFact.text.split(" ").map((word, wIdx) => {
                      const prevCharsCount = currentFact.text.split(" ").slice(0, wIdx).join(" ").length + (wIdx > 0 ? 1 : 0);
                      return (
                        <span key={`w-${wIdx}`} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
                          {word.split("").map((char, index) => {
                            const globalIndex = prevCharsCount + index;
                            return (
                              <span
                                key={`c-${index}`}
                                style={{
                                  display: 'inline-block',
                                  opacity: 0,
                                  transform: 'translateY(2px)',
                                  animation: 'fadeInChar 0.04s ease-out forwards',
                                  animationDelay: `${globalIndex * 0.007}s`,
                                }}
                              >
                                {char}
                              </span>
                            );
                          })}
                          {wIdx < currentFact.text.split(" ").length - 1 && "\u00A0"}
                        </span>
                      );
                    })}
                    "
                  </p>
                  <div style={styles.overlayFactFooter}>
                    <span style={styles.overlayFactSource}>
                      {currentFact.source ? `Source: ${currentFact.source}` : ''}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
      {atsRewriteOffer && (
        <ATSRewriteOffer 
          resumeFile={atsRewriteOffer.resumeFile}
          jdInput={jdInput}
          onRewriteComplete={handleRewriteComplete}
          onDecline={handleDeclineRewrite}
        />
      )}
      
      {/* Upgrade / Paywall Modal */}
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        presaveState={{ jd: jdInput, analysis: analysisResults, evidence: skillEvidence, targets: skillTargets }}
      />

      {tourStep && (
        <>
          <div className="tour-backdrop-scrim" />
          <TourPopover
            step={tourStep}
            targetRef={
              tourStep === 1 ? downloadBtnRef :
                tourStep === 2 ? saveTrackerBtnRef :
                  tourStep === 3 ? historyTabRef : null
            }
            onNext={() => setTourStep(2)}
            onComplete={() => {
              const userTourKey = `hasSeenSaveTrackerTour_${user?.email || 'guest'}`;
              localStorage.setItem(userTourKey, 'true');
              setTourStep(null);
              setTimeout(() => {
                previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
            onSkip={() => {
              const userTourKey = `hasSeenSaveTrackerTour_${user?.email || 'guest'}`;
              localStorage.setItem(userTourKey, 'true');
              setTourStep(null);
            }}
          />
        </>
      )}

      {showLiveModeTour && (
        <div style={modalStyles.overlay}>
          <div className="responsive-modal" style={{ ...modalStyles.modalContent, maxWidth: '800px', padding: '32px' }} onClick={(e) => e.stopPropagation()}>
            <div style={modalStyles.modalHeader}>
              <h3 style={{ margin: '0', fontSize: '1.4rem', color: '#1e293b' }}>
                ✨ Explore Optimization Styles
              </h3>
            </div>
            <div style={modalStyles.modalBody}>
              <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '20px' }}>
                ✨ Discover what your resume bullet could become.
              </p>
              
              <div className="hi-sample-diff-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'stretch' }}>
                {/* Left Column: Interactive Selector */}
                <div style={{ background: 'rgba(248, 250, 252, 0.6)', padding: '24px', borderRadius: '12px', border: '1px solid rgba(226, 232, 240, 0.8)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '240px' }}>
                  <div>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '1.05rem', color: '#1e293b', fontWeight: '600' }}>Slide between, See the Change ⚡</h4>
                    <div className="hi-seg-control" style={{ width: 'fit-content', marginBottom: '16px' }}>
                      {[
                        { id: 'no_change', label: '✨ No Change', cls: 'btn-no-change' },
                        { id: 'surgical', label: '🔧 Surgical', cls: 'btn-surgical' },
                        { id: 'rewrite', label: '✏️ Rewrite', cls: 'btn-rewrite' },
                      ].map(mode => (
                        <button
                          key={mode.id}
                          type="button"
                          className={`hi-seg-btn ${mode.cls}${sampleMode === mode.id ? ' active' : ''}`}
                          onClick={() => {
                            if (sampleMode !== mode.id) {
                              setSampleMode(mode.id);
                              setShowConfetti(false);
                              setTimeout(() => setShowConfetti(true), 10);
                              setTimeout(() => setShowConfetti(false), 1200);
                            }
                          }}
                        >
                          {mode.label}
                        </button>
                      ))}
                    </div>
                    <p style={{ fontSize: '0.85rem', color: '#475569', lineHeight: '1.5' }}>
                      {sampleMode === 'no_change'
                        ? "No changes are made to your original bullet."
                        : sampleMode === 'surgical'
                          ? "Keeps your original bullet and adds a new one tailored to the job."
                          : "Improves your bullet to better match the job using stronger language and relevant skills."}
                    </p>
                  </div>
                </div>

                {/* Right Column: Text comparison output */}
                <div style={{ border: '1px solid rgba(226, 232, 240, 0.8)', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: '240px' }}>
                  <div style={{ background: '#f8fafc', padding: '12px 16px', borderBottom: '1px solid rgba(226, 232, 240, 0.8)', fontSize: '0.8rem', fontWeight: '600', color: '#64748b' }}>
                    RESULT PREVIEW
                  </div>
                  <div style={{ padding: '20px', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', fontSize: '0.9rem', lineHeight: '1.6' }}>
                    {sampleMode === 'no_change' && (
                      <span style={{ color: '#334155' }}>
                        • Implemented a search feature using Python.
                      </span>
                    )}
                    {sampleMode === 'surgical' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ color: '#64748b', textDecoration: 'none' }}>
                          • Implemented a search feature using Python.
                        </span>
                        <span style={{ color: '#0d9488', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          • <span style={{ fontSize: '10px', background: '#e6f4ea', color: '#137333', padding: '2px 6px', borderRadius: '4px', textTransform: 'uppercase', fontWeight: '700' }}>Inserted</span> Built a custom database indexing pipeline using MongoDB to accelerate retrieval times.
                        </span>
                      </div>
                    )}
                    {sampleMode === 'rewrite' && (
                      <span style={{ color: '#ea580c', fontWeight: '500' }}>
                        • Engineered a robust query processing module in Python and MongoDB, improving search latency by 35%.
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-accent-ferrari)',
                    color: '#ffffff',
                    border: 'none',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: isLiveModeTourClosing ? 'scale(1.05)' : 'scale(1)',
                  }}
                  disabled={isLiveModeTourClosing}
                  onClick={() => {
                    setIsLiveModeTourClosing(true);
                    setShowConfetti(true);
                    setTimeout(() => {
                      const userLiveTourKey = `hasSeenLiveModeTour_${user?.email || 'guest'}`;
                      localStorage.setItem(userLiveTourKey, 'true');
                      setShowLiveModeTour(false);
                      setIsLiveModeTourClosing(false);
                      setShowConfetti(false);
                    }, 1200);
                  }}
                >
                  {isLiveModeTourClosing ? "Let's Optimise ⚡" : "Got it 👍 "}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Low Score Rewrite Modal */}
      {showLowScorePopup && (
        <div style={modalStyles.overlay}>
          <div className="responsive-modal" style={{...modalStyles.modalContent, maxWidth: '480px'}} onClick={(e) => e.stopPropagation()}>
            <div style={{...modalStyles.modalHeader, paddingBottom: '16px'}}>
              <h3 style={{...modalStyles.modalTitle, display: 'flex', alignItems: 'center', gap: '8px'}}>
                <span style={{fontSize: '20px'}}>💡</span> Optimization Recommendation
              </h3>
            </div>
            <div style={{...modalStyles.modalBody, paddingTop: '8px'}}>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Your resume could use a refresh to better match this job. <br />We recommend a <strong>quick rewrite</strong> for the best results.
              </p>
              
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowLowScorePopup(false);
                    setForcedRewriteMode(false);
                  }}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                    backgroundColor: 'transparent',
                    color: 'var(--color-text-secondary)',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  className="hover-lift"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const newModes = {};
                    workSections.forEach(s => newModes[s.sectionId] = 'rewrite');
                    projectSections.forEach(s => newModes[s.sectionId] = 'rewrite');
                    setSectionModes(newModes);
                    setForcedRewriteMode(true);
                    setShowLowScorePopup(false);
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#f97316',
                    color: '#ffffff',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  className="hover-lift"
                >
                  Rewrite Completely
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Support / Contact Modal */}
      {isSupportModalOpen && (
        <div style={modalStyles.overlay} onClick={handleCloseSupportModal}>
          <div className="responsive-modal" style={modalStyles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={modalStyles.modalHeader}>
              <h3 style={modalStyles.modalTitle}>Contact Support</h3>
              <button style={modalStyles.modalCloseBtn} onClick={handleCloseSupportModal}>✕</button>
            </div>
            <form onSubmit={handleSendSupport} style={modalStyles.modalBody}>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-secondary)', lineHeight: '1.5' }}>
                Have a question, feedback, or found an issue? Send us a message and we'll get back to you shortly.
              </p>

              {supportSuccess && (
                <div style={{ padding: '12px', backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success)', border: '1px solid var(--color-success-border)', borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: 500 }}>
                  {supportSuccess}
                </div>
              )}
              {supportError && (
                <div style={{ padding: '12px', backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', border: '1px solid var(--color-error-border)', borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: 500 }}>
                  {supportError}
                </div>
              )}

              {!supportSuccess && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Subject Category</label>
                    <select
                      value={selectedSubjectCategory}
                      onChange={(e) => setSelectedSubjectCategory(e.target.value)}
                      style={{
                        padding: '10px 14px',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-border)',
                        fontSize: 'var(--text-sm)',
                        backgroundColor: 'var(--color-surface-alt)',
                        color: 'var(--color-text-primary)',
                        outline: 'none',
                        fontFamily: 'inherit',
                      }}
                      disabled={isSendingSupport}
                    >
                      <option value="General Question">General Question</option>
                      <option value="Billing / Subscription">Billing / Subscription</option>
                      <option value="Resume Scoring">Resume Scoring</option>
                      <option value="Feature Request">Feature Request</option>
                      <option value="Other">Other (Specify below)</option>
                    </select>
                  </div>

                  {selectedSubjectCategory === 'Other' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', animation: 'rz-fadeIn 0.2s ease-out' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Custom Subject</label>
                      <input
                        type="text"
                        placeholder="Type your subject here..."
                        value={customSubjectText}
                        onChange={(e) => setCustomSubjectText(e.target.value)}
                        style={styles.stageInput}
                        required
                        disabled={isSendingSupport}
                      />
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Message</label>
                    <textarea
                      placeholder="Write your message here..."
                      value={supportMessage}
                      onChange={(e) => setSupportMessage(e.target.value)}
                      style={styles.supportTextarea}
                      required
                      disabled={isSendingSupport}
                    />
                  </div>

                  <button
                    type="submit"
                    style={{
                      padding: '12px',
                      backgroundColor: 'var(--color-text-primary)',
                      color: 'var(--color-surface)',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      fontWeight: 700,
                      cursor: 'pointer',
                      marginTop: '10px',
                      transition: 'opacity 0.2s',
                    }}
                    disabled={isSendingSupport}
                    className="hover-lift"
                  >
                    {isSendingSupport ? 'Sending...' : 'Send Message'}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Floating Help / Support FAB */}
      <div
        style={styles.floatingHelpContainer}
        onMouseEnter={() => setIsHelpTooltipVisible(true)}
        onMouseLeave={() => setIsHelpTooltipVisible(false)}
      >
        {isHelpTooltipVisible && (
          <div style={styles.floatingHelpTooltip}>
            Need help? Contact Us
          </div>
        )}
        <button
          onClick={() => setIsSupportModalOpen(true)}
          style={styles.floatingHelpBtn}
          aria-label="Contact Support"
          className="hover-lift"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </button>
      </div>

    </div>
  );
};

const modalStyles = {
  dropdownActionBtn: {
    display: 'block',
    width: '100%',
    padding: '10px 14px',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-text-primary)',
    fontWeight: 600,
    fontSize: 'var(--text-sm)',
    textAlign: 'left',
    cursor: 'pointer',
    marginBottom: '8px',
    transition: 'all var(--transition-fast)',
  },
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    animation: 'rz-fadeInFlat 0.2s ease-out forwards',
  },
  modalContent: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-xl)',
    padding: '32px',
    width: '90%',
    maxWidth: '400px',
    boxShadow: 'var(--shadow-xl)',
    border: '1px solid var(--color-border)',
    animation: 'rz-fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
  },
  modalContentLarge: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-xl)',
    padding: '32px',
    width: '90%',
    maxWidth: '640px',
    boxShadow: 'var(--shadow-xl)',
    border: '1px solid var(--color-border)',
    animation: 'rz-fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  modalTitle: {
    margin: 0,
    fontSize: 'var(--text-xl)',
    fontWeight: 800,
    color: 'var(--color-text-primary)',
    letterSpacing: '-0.02em',
  },
  modalCloseBtn: {
    background: 'var(--color-surface-dark)',
    border: '1px solid var(--color-border)',
    fontSize: '14px',
    color: 'var(--color-text-muted)',
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: 'var(--radius-sm)',
    transition: 'all var(--transition-fast)',
  },
  modalBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  modalBodyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '24px',
  },
  accountRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '12px',
    borderBottom: '1px solid var(--color-border-subtle)',
  },
  accountLabel: {
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-secondary)',
    fontWeight: 600,
  },
  accountValue: {
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-primary)',
    fontWeight: 700,
  },
  accountValueBadge: {
    fontSize: '11px',
    fontWeight: 700,
    backgroundColor: 'var(--color-charcoal-100)',
    color: 'var(--color-charcoal-800)',
    padding: '4px 8px',
    borderRadius: 'var(--radius-sm)',
  },
  pricingCard: {
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    padding: '24px',
    backgroundColor: 'var(--color-surface)',
    display: 'flex',
    flexDirection: 'column',
  },
  pricingCardPro: {
    border: '2px solid var(--color-accent-ferrari)',
    borderRadius: 'var(--radius-lg)',
    padding: '24px',
    backgroundColor: 'var(--color-surface)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    boxShadow: '0 8px 24px var(--color-accent-glow)',
  },
  pricingBadge: {
    position: 'absolute',
    top: '-12px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'var(--color-accent-ferrari)',
    color: '#ffffff',
    fontSize: '10px',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: '4px 12px',
    borderRadius: 'var(--radius-full)',
  },
  pricingName: {
    margin: '0 0 8px 0',
    fontSize: 'var(--text-lg)',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
  },
  pricingNamePro: {
    margin: '0 0 8px 0',
    fontSize: 'var(--text-lg)',
    fontWeight: 800,
    color: 'var(--color-accent-ferrari)',
  },
  pricingPrice: {
    fontSize: '32px',
    fontWeight: 800,
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-heading)',
    lineHeight: 1,
    marginBottom: '24px',
  },
  pricingPricePro: {
    fontSize: '32px',
    fontWeight: 800,
    color: 'var(--color-accent-ferrari)',
    fontFamily: 'var(--font-heading)',
    lineHeight: 1,
    marginBottom: '24px',
  },
  pricingPeriod: { fontSize: '14px', color: 'var(--color-text-muted)', fontWeight: 500 },
  pricingPeriodPro: { fontSize: '14px', color: 'rgba(79, 70, 229, 0.6)', fontWeight: 500 },
  pricingList: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 24px 0',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  pricingFeature: {
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-secondary)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pricingFeaturePro: {
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-primary)',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  checkIcon: { color: 'var(--color-text-muted)', fontSize: '12px' },
  checkIconPro: { color: 'var(--color-accent-ferrari)', fontSize: '12px', fontWeight: 800 },
  pricingBtnSecondary: {
    width: '100%',
    padding: '12px',
    borderRadius: 'var(--radius-full)',
    border: '1px solid var(--color-border-strong)',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-text-primary)',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 'var(--text-sm)',
    transition: 'all var(--transition-fast)',
  },
  pricingBtnPrimary: {
    width: '100%',
    padding: '12px',
    borderRadius: 'var(--radius-full)',
    border: 'none',
    backgroundColor: 'var(--color-accent-ferrari)',
    color: '#ffffff',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: 'var(--text-sm)',
    boxShadow: '0 4px 12px var(--color-accent-glow)',
    transition: 'all var(--transition-fast)',
  },
};

/* ============================================================
   Dashboard Helper Components
   ============================================================ */

/** Animated SVG circular score ring */














// Styles
/* eslint-disable no-dupe-keys */
const styles = {
  supportTextarea: {
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    fontSize: 'var(--text-sm)',
    backgroundColor: 'var(--color-surface-alt)',
    color: 'var(--color-text-primary)',
    outline: 'none',
    fontFamily: 'inherit',
    minHeight: '120px',
    resize: 'vertical',
    transition: 'border-color var(--transition-fast)',
  },
  floatingHelpContainer: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    zIndex: 999,
  },
  floatingHelpTooltip: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)',
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
    boxShadow: 'var(--shadow-md)',
    whiteSpace: 'nowrap',
    animation: 'rz-fadeInFlat 0.2s ease-out forwards',
  },
  floatingHelpBtn: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--color-accent-ferrari) 0%, var(--valley-emerald) 100%)',
    color: '#ffffff',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'var(--shadow-lg)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  overlayBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.35)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    animation: 'rz-fadeInFlat 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
  },
  overlayCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    borderRadius: '24px',
    padding: '40px',
    maxWidth: '540px',
    width: '100%',
    boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    animation: 'rz-fadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
  },
  overlayHeader: {
    marginBottom: '28px',
  },
  overlayStatusKicker: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--color-accent-ferrari)',
    marginBottom: '8px',
  },
  overlayStatusTitle: {
    fontSize: '22px',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    margin: '0 0 8px 0',
  },
  overlayStatusDetail: {
    fontSize: '14px',
    color: 'var(--color-text-secondary)',
    margin: 0,
  },
  overlayProgressContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  overlayStagesRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'center',
    marginTop: '28px',
  },
  overlayStepperWrap: {
    padding: '12px 0',
    width: '100%',
  },
  overlayFactCard: {
    marginTop: '32px',
    padding: '20px 24px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    border: '1.5px solid var(--color-accent-ferrari)',
    borderRadius: '16px',
    width: '100%',
    textAlign: 'left',
    boxSizing: 'border-box',
    boxShadow: '0 12px 24px var(--color-accent-glow)',
  },
  overlayFactHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
    width: '100%',
  },
  overlayFactTitle: {
    fontSize: 'var(--text-xs)',
    fontWeight: 800,
    color: 'var(--color-accent-ferrari)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  overlayFactNextBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--color-accent-ferrari)',
    fontSize: 'var(--text-xs)',
    fontWeight: 800,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 'var(--radius-sm)',
    transition: 'all var(--transition-fast)',
  },
  overlayFactText: {
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-primary)',
    lineHeight: '1.55',
    margin: '4px 0 0 0',
    fontWeight: 600,
  },
  overlayFactFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '12px',
    paddingTop: '8px',
    borderTop: '1px dashed rgba(79, 70, 229, 0.12)',
    width: '100%',
  },
  overlayFactSource: {
    fontSize: '11px',
    color: 'var(--color-text-muted)',
    fontWeight: 600,
    margin: 0,
  },
  overlayFactCounter: {
    fontSize: '10px',
    color: 'var(--color-text-muted)',
    fontWeight: 700,
  },
  scoreRingWrapper: { flexShrink: 0 },
  headerScoreTitle: { fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)', margin: '0 0 8px 0' },
  headerScoreItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
  headerScoreName: { fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 },
  headerScorePercentage: { fontSize: 'var(--text-2xl)', fontWeight: 700, fontFamily: 'var(--font-heading)', lineHeight: 1 },
  stickyDownloadBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 16px',
    backgroundColor: 'var(--color-surface-dark)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-full)',
    color: 'var(--color-text-primary)',
    textDecoration: 'none',
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    transition: 'all 0.2s',
  },
  pdfBtnSpinner: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    border: '2px solid rgba(255,255,255,0.25)',
    borderTopColor: 'var(--color-text-primary)',
    borderRadius: '50%',
    animation: 'rz-spin 0.7s linear infinite',
    flexShrink: 0,
  },
  container: {
    minHeight: '100vh',
    fontFamily: 'var(--font-body)',
  },
  sidebarLogo: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 800,
    letterSpacing: '-1px',
    fontFamily: 'var(--font-heading)',
    color: 'var(--color-text-on-dark)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  sidebarMenu: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '48px',
  },
  sidebarBtn: {
    textAlign: 'left',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--color-charcoal-400)',
    fontSize: 'var(--text-base)',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  sidebarBtnActive: {
    textAlign: 'left',
    padding: '12px 16px',
    backgroundColor: 'var(--color-charcoal-900)',
    borderLeft: '4px solid var(--color-ivory-100)',
    color: 'var(--color-text-on-dark)',
    fontSize: 'var(--text-base)',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  sidebarLogoutBtn: {
    padding: '12px 16px',
    backgroundColor: 'var(--color-charcoal-900)',
    border: '1px solid var(--color-charcoal-800)',
    color: 'var(--color-charcoal-300)',
    marginTop: 'auto',
    cursor: 'pointer',
    fontWeight: 600,
    transition: 'all 0.2s ease',
  },
  brutalistActionBtn: {
    padding: '16px 24px',
    backgroundColor: 'var(--color-charcoal-950)',
    border: 'none',
    color: 'var(--color-text-on-dark)',
    fontSize: 'var(--text-lg)',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
    boxShadow: '4px 4px 0px 0px var(--color-charcoal-900)',
  },
  brutalistResetBtn: {
    padding: '16px 24px',
    backgroundColor: 'transparent',
    border: '2px solid var(--color-charcoal-950)',
    color: 'var(--color-text-primary)',
    fontSize: 'var(--text-lg)',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
  },
  logoMark: {
    color: 'var(--color-charcoal-700)',
    fontSize: '20px',
  },
  experienceSectionsSection: {
    marginTop: '24px',
    padding: '24px',
    backgroundColor: 'var(--color-surface-dark)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
  },
  experienceSectionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  experienceSectionCard: {
    padding: '18px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-xs)',
    transition: 'transform var(--transition-base), box-shadow var(--transition-base)',
  },
  experienceSectionHeader: {
    fontSize: 'var(--text-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
  },
  experienceSectionMeta: {
    marginTop: '4px',
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-muted)',
  },
  experienceSectionBullets: {
    margin: '10px 0 0 20px',
    padding: 0,
  },
  experienceSectionBulletItem: {
    marginBottom: '6px',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-secondary)',
    lineHeight: 'var(--line-height-base)',
  },
  logoutBtn: {
    padding: '9px 18px',
    backgroundColor: 'var(--color-surface-alt)',
    border: '1px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-full)',
    cursor: 'pointer',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    transition: 'all var(--transition-base)',
  },
  mainContent: {
    maxWidth: '1440px',
    margin: '0 auto',
    padding: '40px 24px 64px',
    width: '100%',
    boxSizing: 'border-box',
  },
  introPanel: {
    padding: '28px 32px',
    marginBottom: '24px',
    animation: 'rz-fadeIn 0.35s ease-out',
    backgroundColor: 'rgba(255, 255, 255, 0.45)',
  },
  introTitle: {
    margin: 0,
    fontSize: 'var(--text-3xl)',
    fontFamily: 'var(--font-heading)',
    letterSpacing: '-0.4px',
    lineHeight: 1.2,
  },
  introSubtitle: {
    margin: '8px 0 0 0',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--text-base)',
  },
  workspaceShell: {
    padding: '24px',
    animation: 'rz-fadeIn 0.4s ease-out',
    backgroundColor: 'rgba(255, 255, 255, 0.45)',
  },
  dualColumnSection: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
    marginBottom: '8px',
  },
  sectionColumn: {
    flex: '2 1 420px',
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: '24px',
    minHeight: '360px',
    boxShadow: 'var(--shadow-sm)',
  },
  sectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  sectionTitle: {
    margin: 0,
    fontSize: 'var(--text-xl)',
    fontFamily: 'var(--font-heading)',
    fontWeight: 'var(--font-weight-bold)',
  },
  statusPill: {
    border: '1px solid var(--color-border-strong)',
    backgroundColor: 'var(--color-surface-alt)',
    color: 'var(--color-text-secondary)',
    borderRadius: 'var(--radius-full)',
    padding: '3px 11px',
    fontSize: 'var(--text-xs)',
    fontWeight: 'var(--font-weight-bold)',
    letterSpacing: '0.04em',
  },
  clearPill: {
    border: '1px solid var(--color-error-border)',
    backgroundColor: 'var(--color-error-bg)',
    color: 'var(--color-error)',
    borderRadius: 'var(--radius-full)',
    padding: '3px 11px',
    fontSize: 'var(--text-xs)',
    fontWeight: 'var(--font-weight-bold)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
  },
  collapsibleBtn: {
    width: '100%',
    textAlign: 'left',
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-sm)',
    padding: '10px 12px',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--font-weight-bold)',
    marginBottom: '12px',
    cursor: 'pointer',
    color: 'var(--color-text-secondary)',
    transition: 'all var(--transition-fast)',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 'var(--font-weight-semibold)',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-primary)',
  },
  input: {
    flex: 1,
    padding: '12px 13px',
    border: '1.5px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    outline: 'none',
    transition: 'border-color var(--transition-base), box-shadow var(--transition-base)',
    color: 'var(--color-text-primary)',
  },
  inputGroup: {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px',
  },
  extractBtn: {
    padding: '12px 20px',
    background: 'linear-gradient(135deg var(--in-oklab), var(--color-charcoal-800) 0%, var(--color-charcoal-600) 100%)',
    color: 'var(--color-text-on-dark)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontWeight: 'var(--font-weight-bold)',
    whiteSpace: 'nowrap',
    fontSize: 'var(--text-sm)',
    transition: 'opacity var(--transition-base)',
    boxShadow: 'var(--shadow-sm)',
  },
  urlSection: {
    marginBottom: '20px',
    padding: '14px',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-surface-alt)',
    animation: 'rz-slideDown 0.2s ease-out',
  },
  hint: {
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-muted)',
    margin: '6px 0 0 0',
  },
  textarea: {
    width: '100%',
    padding: '12px 13px',
    border: '1.5px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    minHeight: '260px',
    boxSizing: 'border-box',
    marginBottom: '8px',
    resize: 'vertical',
    color: 'var(--color-text-primary)',
    lineHeight: 'var(--line-height-base)',
    transition: 'border-color var(--transition-base), box-shadow var(--transition-base)',
    outline: 'none',
  },
  uploadArea: {
    border: '2px dashed var(--color-border-strong)',
    borderRadius: 'var(--radius-lg)',
    padding: '44px 24px',
    textAlign: 'center',
    backgroundColor: 'var(--color-surface-alt)',
    position: 'relative',
    cursor: 'pointer',
    transition: 'border-color var(--transition-base), background-color var(--transition-base), box-shadow var(--transition-base)',
    minHeight: '240px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'rz-pulseBorder 3s ease-in-out infinite',
  },
  fileInput: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer',
    top: 0,
    left: 0,
  },
  uploadContent: {
    pointerEvents: 'none',
  },
  uploadText: {
    margin: '0',
    fontSize: 'var(--text-lg)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-heading)',
  },
  inlineLoadingWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
  },
  smallSpinner: {
    width: '18px',
    height: '18px',
    border: '3px solid var(--color-border)',
    borderTopColor: 'var(--color-charcoal-700)',
    borderRadius: '50%',
    animation: 'rz-spin 0.9s linear infinite',
  },
  statusMessage: {
    padding: '12px 16px',
    backgroundColor: 'var(--color-surface-alt)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    margin: '12px 0 0 0',
    fontSize: 'var(--text-sm)',
    textAlign: 'center',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-secondary)',
    animation: 'rz-fadeInFlat 0.2s ease-out',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '26px 16px',
    marginTop: '8px',
  },
  buttonDock: {
    marginTop: '10px',
    borderTop: '1px solid var(--color-border-subtle)',
    paddingTop: '14px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
  },
  analyzeBtn: {
    flex: 1,
    padding: '14px',
    background: 'linear-gradient(135deg var(--in-oklab), var(--color-charcoal-800) 0%, var(--color-charcoal-600) 100%)',
    color: 'var(--color-text-on-dark)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontWeight: 'var(--font-weight-bold)',
    fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-heading)',
    letterSpacing: '0.01em',
    boxShadow: '0 4px 14px rgba(13,15,13,0.18)',
    transition: 'opacity var(--transition-base), transform var(--transition-base), box-shadow var(--transition-base)',
  },
  resetBtn: {
    flex: 1,
    padding: '14px',
    backgroundColor: 'var(--color-surface-alt)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontWeight: 'var(--font-weight-semibold)',
    fontSize: 'var(--text-base)',
    transition: 'all var(--transition-base)',
  },
  resultsContainer: {
    animation: 'rz-fadeIn 0.4s ease-out',
  },
  resultsHeading: {
    fontFamily: 'var(--font-heading)',
    fontSize: 'var(--text-2xl)',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    marginBottom: '20px',
    letterSpacing: '-0.3px',
  },
  scoreCard: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    padding: '32px',
    borderRadius: 'var(--radius-xl)',
    marginBottom: '24px',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    boxShadow: 'var(--shadow-sm)',
  },
  detailedScores: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid var(--color-border-subtle)',
  },
  detailedBreakdownSub: {
    margin: '4px 0 16px 0',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--text-sm)',
    lineHeight: 'var(--line-height-base)',
  },
  scoreDesc: {
    fontSize: '11px',
    color: 'var(--color-text-muted)',
    marginTop: '2px',
    marginBottom: '6px',
    lineHeight: '1.3',
  },
  scoreGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginTop: '14px',
  },
  scoreItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '14px 16px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-xs)',
  },
  scoreName: {
    fontSize: 'var(--text-xs)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  scoreBar: {
    width: '100%',
    height: '6px',
    backgroundColor: 'var(--color-border)',
    borderRadius: 'var(--radius-full)',
    overflow: 'hidden',
  },
  scoreBarFill: {
    height: '100%',
    backgroundColor: 'var(--color-charcoal-700)',
    borderRadius: 'var(--radius-full)',
    transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)',
    animation: 'rz-growBar 1s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  scorePercentage: {
    fontSize: 'var(--text-lg)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-heading)',
  },
  scoreReasonBox: {
    marginTop: '6px',
    padding: '8px 10px',
    backgroundColor: 'var(--color-surface-alt)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    animation: 'rz-slideDown 0.25s ease-out',
  },
  scoreReasonLabel: {
    fontSize: '9px',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  scoreReasonText: {
    fontSize: '11px',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.4',
    margin: 0,
  },
  summarySection: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    padding: '20px 22px',
    borderRadius: 'var(--radius-lg)',
    marginBottom: '24px',
    boxShadow: 'var(--shadow-xs)',
  },
  summaryText: {
    lineHeight: 'var(--line-height-loose)',
    color: 'var(--color-text-secondary)',
    margin: '10px 0 0 0',
    fontSize: 'var(--text-sm)',
  },
  improvementSection: {
    marginBottom: '24px',
    padding: '20px',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-xl)',
    background: 'linear-gradient(135deg var(--in-oklab), var(--color-surface) 0%, var(--color-surface-alt) 100%)',
    boxShadow: 'var(--shadow-md)',
  },
  improvementHint: {
    margin: '8px 0 16px 0',
    color: 'var(--color-text-secondary)',
    lineHeight: 'var(--line-height-loose)',
    fontSize: 'var(--text-sm)',
  },
  improvementList: {
    display: 'grid',
    gap: '10px',
  },
  improvementItemSkill: {
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-surface)',
    padding: '14px',
    boxShadow: 'var(--shadow-xs)',
  },
  improvementItemPlain: {
    border: '1px solid var(--color-border-subtle)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-surface-alt)',
    color: 'var(--color-text-secondary)',
    padding: '12px 14px',
    lineHeight: 'var(--line-height-base)',
    fontSize: 'var(--text-sm)',
  },
  improvementSkillHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-heading)',
  },
  skillDetailStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px dashed var(--color-border)',
  },
  skillAiInfoCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    background: 'color-mix(in srgb, var(--color-accent-ferrari) 6%, var(--color-surface))',
    border: '1px solid color-mix(in srgb, var(--color-accent-ferrari) 20%, transparent)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 12px',
  },
  skillAiInfoIcon: {
    fontSize: '12px',
    color: 'var(--color-accent-ferrari)',
    flexShrink: 0,
    marginTop: '1px',
  },
  skillAiInfoText: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.5',
  },
  improvementSkillText: {
    margin: '8px 0 0 0',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--text-sm)',
    lineHeight: 'var(--line-height-base)',
  },
  /* ── Optimization Preview — Full Resume Layout ── */
  previewHeader: {
    borderBottom: '1px solid var(--color-border)',
    paddingBottom: '14px',
    marginBottom: '14px',
  },
  previewHeaderLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  previewBadge: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--color-charcoal-600)',
    backgroundColor: 'var(--color-charcoal-050)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-full)',
    padding: '2px 8px',
    marginBottom: '6px',
    width: 'fit-content',
  },
  previewTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 800,
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-heading)',
    letterSpacing: '-0.3px',
  },
  previewHeadline: {
    margin: '3px 0 0 0',
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
    lineHeight: 1.4,
  },
  previewContact: {
    margin: '6px 0 0 0',
    fontSize: '12px',
    color: 'var(--color-text-muted)',
    lineHeight: 1.4,
  },
  previewSection: {
    marginTop: '16px',
    paddingTop: '14px',
    borderTop: '1px solid var(--color-border-subtle)',
  },
  previewSectionLabel: {
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--color-text-muted)',
    marginBottom: '10px',
  },
  previewSectionHint: {
    margin: '-2px 0 8px 0',
    fontSize: '12px',
    lineHeight: 1.4,
    color: 'var(--color-text-muted)',
  },
  previewSummaryText: {
    margin: 0,
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.6,
  },
  previewSkillsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  previewSkillPill: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--color-charcoal-800)',
    backgroundColor: 'var(--color-charcoal-050)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-full)',
    padding: '3px 10px',
  },
  previewRoleCard: {
    marginTop: '12px',
    padding: '12px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-surface)',
  },
  previewRoleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '8px',
    flexWrap: 'wrap',
  },
  previewRoleLeft: {
    flex: 1,
    minWidth: 0,
  },
  previewRoleTitle: {
    fontWeight: 700,
    fontSize: '14px',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-heading)',
  },
  previewRoleCompany: {
    fontSize: '14px',
    color: 'var(--color-text-secondary)',
    fontWeight: 500,
  },
  previewRoleDate: {
    fontSize: '12px',
    color: 'var(--color-text-muted)',
    whiteSpace: 'nowrap',
    fontWeight: 500,
    flexShrink: 0,
  },
  previewBulletList: {
    margin: '4px 0 0 0',
    paddingLeft: '18px',
    listStyle: 'disc',
  },
  previewBulletItem: {
    marginBottom: '5px',
    color: 'var(--color-text-secondary)',
    fontSize: '12.5px',
    lineHeight: 1.5,
  },
  previewBulletItemNew: {
    marginBottom: '5px',
    color: 'var(--color-text-primary)',
    fontSize: '12.5px',
    lineHeight: 1.5,
    backgroundColor: 'var(--color-success-bg)',
    borderRadius: '4px',
    padding: '3px 6px',
    marginLeft: '-6px',
    listStylePosition: 'inside',
    border: '1px solid var(--color-success-border)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
  },
  previewNewBadge: {
    display: 'inline-block',
    fontSize: '9px',
    fontWeight: 800,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: 'var(--color-success)',
    backgroundColor: 'var(--color-success-bg)',
    border: '1px solid var(--color-success-border)',
    borderRadius: 'var(--radius-full)',
    padding: '1px 6px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    marginTop: '2px',
  },
  previewEduRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    flexWrap: 'wrap',
    marginTop: '8px',
  },
  previewEduLeft: {
    flex: 1,
    minWidth: 0,
  },
  previewEduDegree: {
    fontSize: '12.5px',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
  },
  previewEduInst: {
    fontSize: '12.5px',
    color: 'var(--color-text-secondary)',
    fontWeight: 400,
  },
  previewTraceItem: {
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 12px',
    marginTop: '8px',
    backgroundColor: 'var(--color-surface)',
  },
  previewTraceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },
  previewTraceSkillName: {
    fontSize: '13px',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
  },
  previewTraceApplied: {
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--color-success)',
    backgroundColor: 'var(--color-success-bg)',
    border: '1px solid var(--color-success-border)',
    borderRadius: 'var(--radius-full)',
    padding: '2px 8px',
    letterSpacing: '-0.8px',
    fontFamily: 'var(--font-heading)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  logoMark: {
    color: 'var(--color-accent-ferrari)',
    fontSize: '18px',
  },
  navLogo: {
    margin: 0,
    fontSize: 'var(--text-sm)',
    fontWeight: 800,
    letterSpacing: '-0.04em',
    color: 'var(--color-text-primary)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: 'var(--font-heading)',
    lineHeight: 1,
  },
  navMenu: {
    display: 'flex',
    gap: '4px',
  },
  navBtn: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: 'var(--radius-full)',
    transition: 'all var(--transition-fast)',
  },
  navBtnActive: {
    padding: '8px 16px',
    backgroundColor: 'var(--color-accent-ferrari)',
    border: 'none',
    color: '#ffffff',
    fontSize: 'var(--text-sm)',
    fontWeight: 700,
    cursor: 'pointer',
    borderRadius: 'var(--radius-full)',
    boxShadow: 'var(--shadow-sm)',
  },
  navLogoutBtn: {
    padding: '6px 14px',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-full)',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 'var(--text-xs)',
    transition: 'all var(--transition-fast)',
  },
  profileTrigger: {
    padding: 0,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 'var(--radius-full)',
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  profileAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: 'var(--radius-full)',
    objectFit: 'cover',
    border: '1.5px solid var(--color-border-strong)',
    boxShadow: 'var(--shadow-sm)',
    transition: 'transform var(--transition-fast)',
  },
  profileInitialsAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--color-accent-ferrari)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 700,
    border: '1.5px solid var(--color-border-strong)',
    boxShadow: 'var(--shadow-sm)',
    transition: 'transform var(--transition-fast)',
  },
  profileHoverTooltip: {
    position: 'absolute',
    top: '46px',
    right: '0',
    backgroundColor: '#ffffff',
    border: '1px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-md)',
    padding: '8px 12px',
    whiteSpace: 'nowrap',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
    zIndex: 150,
    pointerEvents: 'none',
  },
  tooltipArrow: {
    position: 'absolute',
    top: '-5px',
    right: '14px',
    width: '8px',
    height: '8px',
    backgroundColor: '#ffffff',
    borderLeft: '1px solid var(--color-border-strong)',
    borderTop: '1px solid var(--color-border-strong)',
    transform: 'rotate(45deg)',
  },
  accountDropdown: {
    position: 'absolute',
    top: '46px',
    right: 0,
    width: '260px',
    backgroundColor: '#ffffff',
    border: '1px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-xl)',
    boxShadow: 'var(--shadow-lg)',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    zIndex: 200,
    textAlign: 'left',
  },
  accountDropdownHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  dropdownAvatarLarge: {
    flexShrink: 0,
  },
  profileAvatarLarge: {
    width: '40px',
    height: '40px',
    borderRadius: 'var(--radius-full)',
    objectFit: 'cover',
    border: '1px solid var(--color-border)',
  },
  profileInitialsAvatarLarge: {
    width: '40px',
    height: '40px',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--color-accent-ferrari)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    fontWeight: 700,
  },
  dropdownUserInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  dropdownUserName: {
    fontSize: 'var(--text-sm)',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: 0,
  },
  dropdownUserEmail: {
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-muted)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: 0,
  },
  dropdownDivider: {
    height: '1px',
    backgroundColor: 'var(--color-border)',
    margin: '4px 0',
  },
  dropdownSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  dropdownSectionLabel: {
    fontSize: '10px',
    fontWeight: 800,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '2px',
    textAlign: 'left',
  },
  dropdownItemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 'var(--text-xs)',
  },
  dropdownItemKey: {
    color: 'var(--color-text-secondary)',
  },
  dropdownItemVal: {
    fontWeight: 600,
    color: 'var(--color-text-primary)',
  },
  dropdownItemValBadge: {
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--color-accent-ferrari)',
    backgroundColor: 'var(--color-accent-glow)',
    padding: '2px 8px',
    borderRadius: 'var(--radius-full)',
  },
  dropdownLogoutBtn: {
    padding: '10px',
    width: '100%',
    backgroundColor: 'var(--color-surface-dark)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 'var(--text-xs)',
    textAlign: 'center',
    transition: 'all var(--transition-fast)',
  },
  greetingText: {
    display: 'inline-block',
    fontWeight: 600,
    color: 'var(--color-accent-ferrari)',
  },
  wellnessBanner: {
    margin: '0 auto 24px auto',
    maxWidth: '1280px',
    padding: '12px 16px',
    backgroundColor: 'var(--color-success-bg)',
    border: '1px solid var(--color-success-border)',
    borderRadius: 'var(--radius-lg)',
    color: 'var(--color-success)',
    display: 'flex',
    alignItems: 'center',
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    boxShadow: 'var(--shadow-sm)',
  },
  wellnessCloseBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--color-success)',
    cursor: 'pointer',
    fontSize: '14px',
    marginLeft: '12px',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  welcomeCreditBanner: {
    margin: '0 auto 24px auto',
    maxWidth: '1280px',
    padding: '12px 16px',
    backgroundColor: 'rgba(227, 10, 23, 0.08)',
    border: '1px solid rgba(227, 10, 23, 0.15)',
    borderRadius: 'var(--radius-lg)',
    color: 'var(--color-accent-ferrari)',
    display: 'flex',
    alignItems: 'center',
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    boxShadow: 'var(--shadow-sm)',
  },
  welcomeCreditCloseBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--color-accent-ferrari)',
    cursor: 'pointer',
    padding: '4px',
    fontSize: '14px',
    marginLeft: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.75)',
    animation: 'rz-fadeInFlat 0.3s ease-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    willChange: 'opacity',
    transform: 'translateZ(0)',
  },
  welcomeCreditModal: {
    backgroundColor: '#ffffff',
    border: '2px solid rgba(79, 70, 229, 0.2)',
    borderRadius: 'var(--radius-xl)',
    padding: '36px 40px',
    maxWidth: '440px',
    width: '90%',
    textAlign: 'center',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
    position: 'relative',
    overflow: 'visible',
    willChange: 'transform, opacity',
  },
  modalEmoji: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: '10px',
    fontFamily: 'var(--font-heading)',
  },
  modalText: {
    fontSize: 'var(--text-sm)',
    color: '#475569',
    lineHeight: '1.6',
    marginBottom: '26px',
  },
  modalCloseBtn: {
    padding: '12px 28px',
    backgroundColor: '#4f46e5',
    color: '#ffffff',
    border: 'none',
    borderRadius: 'var(--radius-full)',
    fontWeight: 700,
    fontSize: 'var(--text-sm)',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(79, 70, 229, 0.35)',
    transition: 'all 0.2s ease',
  },


  // Workspace Stage Styles
  stageTitle: {
    fontSize: 'var(--text-3xl)',
    fontFamily: 'var(--font-heading)',
    fontWeight: 800,
    color: 'var(--color-text-primary)',
    margin: '0 0 10px 0',
    letterSpacing: '-0.05em',
    lineHeight: 1.15,
    textAlign: 'center',
  },
  stageSubtitle: {
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-muted)',
    margin: '0 0 36px 0',
    textAlign: 'center',
    fontWeight: 400,
    letterSpacing: '0.01em',
  },
  stageLeftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  stageRightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  stageSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  stageSectionTitle: {
    margin: 0,
    fontSize: 'var(--text-sm)',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    letterSpacing: '-0.01em',
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--color-accent-ferrari)',
    fontSize: 'var(--text-xs)',
    fontWeight: 700,
    cursor: 'pointer',
    padding: '4px 8px',
  },
  statusBadgeRequired: {
    fontSize: '9px',
    fontWeight: 700,
    color: 'var(--color-text-muted)',
    backgroundColor: 'var(--color-surface-dark)',
    border: '1px solid var(--color-border)',
    padding: '2px 9px',
    borderRadius: 'var(--radius-full)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  extractorContainer: {
    marginBottom: '4px',
  },
  collapsibleBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    cursor: 'pointer',
    padding: '4px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'color var(--transition-fast)',
  },
  urlSection: {
    marginTop: '6px',
    animation: 'rz-fadeIn 0.2s ease-out',
  },
  urlInputGroup: {
    display: 'flex',
    gap: '8px',
  },
  stageInput: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    fontSize: 'var(--text-sm)',
    backgroundColor: 'var(--color-surface-alt)',
    color: 'var(--color-text-primary)',
    outline: 'none',
    transition: 'border-color var(--transition-fast)',
    fontFamily: 'inherit',
  },
  extractBtn: {
    padding: '10px 16px',
    backgroundColor: 'var(--color-surface-dark)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    fontWeight: 600,
    fontSize: 'var(--text-xs)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all var(--transition-fast)',
  },
  hiddenLabel: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    border: '0',
  },
  stageTextarea: {
    width: '100%',
    minHeight: '280px',
    padding: '16px',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-text-primary)',
    fontSize: 'var(--text-sm)',
    lineHeight: '1.6',
    outline: 'none',
    resize: 'none',
    boxSizing: 'border-box',
    transition: 'border-color var(--transition-base), box-shadow var(--transition-base)',
    fontFamily: 'inherit',
    caretColor: 'var(--color-accent-ferrari)',
  },
  textareaFooter: {
    fontSize: '11px',
    color: 'var(--color-text-muted)',
    textAlign: 'right',
    marginTop: '4px',
    letterSpacing: '0.01em',
  },

  // File Upload Dropzone
  uploadDropZone: {
    position: 'relative',
    border: '1px dashed var(--color-border-strong)',
    borderRadius: 'var(--radius-xl)',
    padding: '40px var(--space-6)',
    textAlign: 'center',
    backgroundColor: 'var(--color-surface-alt)',
    transition: 'border-color var(--transition-base), background-color var(--transition-base)',
    cursor: 'pointer',
    minHeight: '180px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileInput: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer',
    zIndex: 2,
  },
  uploadZoneContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  uploadIcon: {
    fontSize: '28px',
  },
  uploadMainText: {
    margin: 0,
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
  },
  uploadSubText: {
    margin: 0,
    fontSize: '11px',
    color: 'var(--color-text-muted)',
    marginTop: '3px',
  },
  uploadLoading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    color: 'var(--color-text-primary)',
    fontWeight: 600,
    fontSize: 'var(--text-sm)',
  },
  spinner: {
    width: '24px',
    height: '24px',
    border: '3px solid rgba(255, 255, 255, 0.08)',
    borderTopColor: 'var(--color-accent-ferrari)',
    borderRadius: '50%',
    animation: 'rz-spin 0.8s linear infinite',
  },

  // CTA Button Area
  ctaArea: {
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  statusBanner: {
    padding: '12px 16px',
    border: '1px solid',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    lineHeight: '1.4',
  },
  loadingWrap: {
    padding: '8px 0',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
  },
  actionBtnPrimary: {
    flex: 1,
    padding: '16px 24px',
    backgroundColor: 'var(--color-accent-ferrari)',
    color: '#ffffff',
    border: 'none',
    borderRadius: 'var(--radius-full)',
    fontSize: 'var(--text-base)',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all var(--transition-base)',
    boxShadow: '0 8px 24px var(--color-accent-glow)',
  },
  actionBtnSecondary: {
    padding: '16px 24px',
    backgroundColor: 'var(--color-surface-dark)',
    border: '1px solid var(--color-border)',
    color: 'var(--color-text-primary)',
    borderRadius: 'var(--radius-full)',
    fontSize: 'var(--text-base)',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all var(--transition-base)',
  },

  // Results Styles
  resultsContainer: {
    width: '100%',
    animation: 'rz-fadeIn 0.5s var(--transition-spring)',
  },
  statusMessage: {
    padding: '14px 20px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-surface-dark)',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-primary)',
    marginBottom: '20px',
    fontWeight: 500,
  },
  resultsHeading: {
    fontSize: 'var(--text-3xl)',
    fontFamily: 'var(--font-heading)',
    fontWeight: 800,
    color: 'var(--color-text-primary)',
    marginBottom: '0',
    letterSpacing: '-0.05em',
    lineHeight: 1.1,
    paddingBottom: '20px',
    borderBottom: '1px solid var(--color-border)',
  },
  scoreCard: {
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-surface-dark)',
  },
  detailedScores: {
    padding: '24px',
    backgroundColor: 'var(--color-surface-dark)',
  },
  sidebarSectionTitle: {
    margin: '0 0 16px 0',
    fontSize: 'var(--text-xs)',
    fontWeight: 800,
    color: 'var(--color-text-secondary)',
    fontFamily: 'var(--font-heading)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid var(--color-border)',
    paddingBottom: '8px',
  },
  sidebarScoreList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sidebarScoreItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sidebarScoreHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  scoreName: {
    fontSize: 'var(--text-xs)',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
  },
  sidebarScorePercentage: {
    fontSize: 'var(--text-sm)',
    fontWeight: 800,
    fontFamily: 'var(--font-heading)',
  },
  scoreBar: {
    width: '100%',
    height: '6px',
    backgroundColor: 'var(--color-border)',
    borderRadius: 'var(--radius-full)',
    overflow: 'hidden',
  },
  scoreBarFill: {
    height: '100%',
    borderRadius: 'var(--radius-full)',
    transition: 'width 1s cubic-bezier(0.16, 1, 0.3, 1)',
  },
  scoreReasonBox: {
    marginTop: '6px',
    padding: '8px 10px',
    borderRadius: 'var(--radius-sm)',
    backgroundColor: 'var(--color-surface)',
  },
  scoreReasonLabel: {
    fontSize: '9px',
    fontWeight: 800,
    color: 'var(--color-accent-ferrari)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  scoreReasonText: {
    margin: '2px 0 0 0',
    fontSize: '11px',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.4',
  },
  sidebarActionsCard: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    backgroundColor: 'var(--color-surface-dark)',
  },
  optimizeBtn: {
    width: '100%',
    padding: '14px',
    backgroundColor: 'var(--color-accent-ferrari)',
    color: '#ffffff',
    border: 'none',
    borderRadius: 'var(--radius-full)',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-heading)',
    boxShadow: '0 4px 14px var(--color-accent-glow)',
    transition: 'all var(--transition-base)',
  },
  newAnalysisBtn: {
    width: '100%',
    padding: '14px',
    backgroundColor: 'transparent',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-full)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 'var(--text-sm)',
    transition: 'all var(--transition-fast)',
  },
  sidebarDownloadsCard: {
    padding: '20px',
    backgroundColor: 'var(--color-surface-dark)',
  },
  artifactGridSidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  artifactTileSidebar: {
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    textDecoration: 'none',
    transition: 'all var(--transition-base)',
  },
  artifactTileBadgeSidebar: {
    width: '32px',
    height: '32px',
    borderRadius: 'var(--radius-sm)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-accent-ferrari)',
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: 800,
    flexShrink: 0,
  },
  artifactTileBodySidebar: {
    flex: 1,
    minWidth: 0,
  },
  artifactTileTitleSidebar: {
    fontSize: 'var(--text-xs)',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
  },
  artifactTileFileSidebar: {
    fontSize: '11px',
    color: 'var(--color-text-muted)',
    marginTop: '1px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // Main Column Results Details
  summarySection: {
    padding: '24px',
    backgroundColor: 'var(--color-surface-dark)',
  },
  mainSectionTitle: {
    margin: '0 0 16px 0',
    fontSize: 'var(--text-sm)',
    fontWeight: 800,
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-heading)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  summaryText: {
    margin: 0,
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.6',
  },
  improvementSection: {
    padding: '24px',
    backgroundColor: 'var(--color-surface-dark)',
  },
  improvementHint: {
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.5',
    marginBottom: '20px',
  },
  improvementList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  improvementItemPlain: {
    padding: '14px',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.5',
  },
  improvementSkillText: {
    margin: '6px 0 0 0',
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.45',
  },
  skillDetailStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px dashed var(--color-border)',
  },
  skillAiInfoCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    background: 'color-mix(in srgb, var(--color-accent-ferrari) 6%, var(--color-surface))',
    border: '1px solid color-mix(in srgb, var(--color-accent-ferrari) 20%, transparent)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 12px',
  },
  skillAiInfoIcon: {
    fontSize: '12px',
    color: 'var(--color-accent-ferrari)',
    flexShrink: 0,
    marginTop: '1px',
  },
  skillAiInfoText: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.5',
  },
  skillEvidenceTextarea: {
    width: '100%',
    minHeight: '80px',
    border: '1px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 12px',
    fontSize: 'var(--text-xs)',
    lineHeight: '1.45',
    boxSizing: 'border-box',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-text-primary)',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  sectionPickerWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sectionPickerLabel: {
    fontSize: '10px',
    fontWeight: 800,
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },

  // Optimization Panels
  optimizationPanel: {
    padding: '24px',
    backgroundColor: 'var(--color-surface-dark)',
  },
  optimizationStatusBox: {
    marginTop: '8px',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--color-surface)',
    padding: '18px',
  },
  optimizationHeroRow: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
  },
  optimizationStatusHeader: {
    flex: 1,
    minWidth: '250px',
  },
  optimizationStatusKicker: {
    display: 'inline-block',
    fontSize: '9px',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontWeight: 800,
    color: 'var(--color-accent-ferrari)',
    marginBottom: '6px',
  },
  optimizationStatusTitle: {
    fontSize: 'var(--text-lg)',
    fontWeight: 800,
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-heading)',
    lineHeight: 1.25,
  },
  optimizationStatusDetail: {
    margin: '4px 0 0 0',
    fontSize: 'var(--text-xs)',
    lineHeight: 1.5,
    color: 'var(--color-text-secondary)',
  },
  optimizationStageRow: {
    marginTop: '12px',
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  optimizationStageChip: {
    borderRadius: 'var(--radius-full)',
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-surface-alt)',
    color: 'var(--color-text-muted)',
    padding: '4px 10px',
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.02em',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  optimizationStageChipCurrent: {
    borderRadius: 'var(--radius-full)',
    border: '1px solid rgba(79, 70, 229, 0.3)',
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    color: 'var(--color-accent-ferrari)',
    padding: '4px 10px',
    fontSize: '9px',
    fontWeight: 800,
    letterSpacing: '0.02em',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  optimizationStageChipDone: {
    borderRadius: 'var(--radius-full)',
    border: '1px solid rgba(48, 209, 88, 0.3)',
    backgroundColor: 'rgba(48, 209, 88, 0.1)',
    color: '#16a34a',
    padding: '4px 10px',
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.02em',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  optimizationStageChipLabel: {
    lineHeight: 1,
  },
  optimizationStageLiveDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-accent-ferrari)',
    boxShadow: '0 0 8px rgba(79, 70, 229, 0.5)',
  },
  optimizationOrbit: {
    width: '84px',
    height: '84px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  optimizationOrbitInner: {
    width: '68px',
    height: '68px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  optimizationOrbitValue: {
    fontSize: '16px',
    fontWeight: 800,
    fontFamily: 'var(--font-heading)',
    color: 'var(--color-text-primary)',
  },
  optimizationProgressTrack: {
    marginTop: '14px',
    width: '100%',
    height: '8px',
    borderRadius: 'var(--radius-full)',
    overflow: 'hidden',
    backgroundColor: 'var(--color-border)',
  },
  optimizationProgressFill: {
    height: '100%',
    borderRadius: 'var(--radius-full)',
    background: 'linear-gradient(90deg, var(--color-accent-ferrari) 0%, #818cf8 100%)',
    boxShadow: '0 0 10px var(--color-accent-glow)',
    transition: 'width 400ms cubic-bezier(0.16, 1, 0.3, 1)',
  },
  optimizationStepCode: {
    margin: '8px 0 0 0',
    fontSize: '10px',
    color: 'var(--color-text-muted)',
    textTransform: 'capitalize',
    letterSpacing: '0.02em',
  },
  retryBtn: {
    marginTop: '12px',
    padding: '10px 16px',
    borderRadius: 'var(--radius-full)',
    border: '1px solid var(--color-border-strong)',
    backgroundColor: 'transparent',
    fontWeight: 600,
    cursor: 'pointer',
    color: 'var(--color-text-primary)',
    fontSize: 'var(--text-xs)',
    transition: 'all var(--transition-fast)',
  },
  saveTrackerBtn: {
    marginTop: '12px',
    padding: '12px 20px',
    borderRadius: 'var(--radius-full)',
    border: 'none',
    color: '#ffffff',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all var(--transition-base)',
    fontSize: 'var(--text-xs)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  },

  // Experience Sections List
  experienceSectionsSection: {
    padding: '24px',
    backgroundColor: 'var(--color-surface-dark)',
  },
  experienceSectionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  experienceSectionCard: {
    padding: '16px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-xs)',
  },
  experienceSectionHeader: {
    fontSize: 'var(--text-sm)',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
  },
  experienceSectionMeta: {
    marginTop: '2px',
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-secondary)',
  },
  experienceSectionBullets: {
    margin: '10px 0 0 16px',
    padding: 0,
  },
  experienceSectionBulletItem: {
    marginBottom: '6px',
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.5',
  },

  // High Fidelity Paper Preview
  paperContainer: {
    backgroundColor: '#ffffff',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
    padding: '48px var(--space-8)',
    borderRadius: 'var(--radius-lg)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    color: '#1d1d1f',
    lineHeight: '1.55',
    marginTop: '16px',
  },
  previewHeader: {
    borderBottom: '2px solid #1d1d1f',
    paddingBottom: '16px',
    marginBottom: '24px',
  },
  previewHeaderLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  previewBadge: {
    alignSelf: 'flex-start',
    fontSize: '9px',
    fontWeight: 800,
    color: 'var(--color-accent-ferrari)',
    backgroundColor: 'rgba(227, 10, 23, 0.08)',
    border: '1px solid rgba(227, 10, 23, 0.15)',
    padding: '2px 8px',
    borderRadius: 'var(--radius-full)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: '4px',
  },
  previewTitle: {
    margin: 0,
    fontSize: 'var(--text-2xl)',
    color: '#1d1d1f',
    fontWeight: 800,
    letterSpacing: '-0.03em',
  },
  previewHeadline: {
    margin: 0,
    fontSize: 'var(--text-sm)',
    color: '#515154',
    fontWeight: 600,
  },
  previewContact: {
    margin: '4px 0 0 0',
    fontSize: '11px',
    color: '#86868b',
  },
  previewSection: {
    marginBottom: '24px',
  },
  previewSectionLabel: {
    fontSize: '11px',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#1d1d1f',
    borderBottom: '1px solid #e5e5e7',
    paddingBottom: '6px',
    marginBottom: '12px',
  },
  previewSummaryText: {
    margin: 0,
    fontSize: '12px',
    color: '#3a3a3c',
    lineHeight: '1.6',
  },
  previewSkillsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  previewSkillPill: {
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor: '#f5f5f7',
    color: '#1d1d1f',
    padding: '4px 10px',
    borderRadius: 'var(--radius-full)',
  },
  previewSectionHint: {
    margin: '0 0 12px 0',
    fontSize: '11px',
    color: 'var(--color-accent-ferrari)',
    fontWeight: 600,
  },
  previewRoleCard: {
    marginBottom: '16px',
  },
  previewRoleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '6px',
  },
  previewRoleLeft: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
  },
  previewRoleTitle: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#1d1d1f',
  },
  previewRoleCompany: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#515154',
  },
  previewRoleDate: {
    fontSize: '11px',
    color: '#86868b',
    fontWeight: 500,
  },
  previewBulletList: {
    margin: 0,
    paddingLeft: '16px',
  },
  previewBulletItem: {
    fontSize: '12px',
    color: '#3a3a3c',
    lineHeight: '1.6',
    marginBottom: '4px',
  },
  previewBulletItemNew: {
    fontSize: '12px',
    color: '#1d1d1f',
    lineHeight: '1.6',
    marginBottom: '6px',
    backgroundColor: 'rgba(227, 10, 23, 0.05)',
    borderLeft: '3px solid var(--color-accent-ferrari)',
    padding: '4px 8px',
    borderRadius: '0 4px 4px 0',
    listStyleType: 'none',
  },
  previewNewBadge: {
    display: 'inline-block',
    fontSize: '8px',
    fontWeight: 900,
    color: '#ffffff',
    backgroundColor: 'var(--color-accent-ferrari)',
    padding: '1px 5px',
    borderRadius: '2px',
    marginLeft: '6px',
    verticalAlign: 'middle',
    letterSpacing: '0.04em',
  },
  previewEduRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '8px',
  },
  previewEduLeft: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
  },
  previewEduDegree: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#1d1d1f',
  },
  previewEduInst: {
    fontSize: '12px',
    color: '#515154',
  },
  previewTraceItem: {
    padding: '12px',
    borderRadius: 'var(--radius-md)',
    backgroundColor: '#f5f5f7',
    border: '1px solid #e5e5e7',
    marginBottom: '10px',
  },
  previewTraceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  previewTraceSkillName: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#1d1d1f',
  },
  previewTraceApplied: {
    fontSize: '9px',
    fontWeight: 800,
    color: '#30d158',
    backgroundColor: 'rgba(48, 209, 88, 0.1)',
    border: '1px solid rgba(48, 209, 88, 0.2)',
    borderRadius: 'var(--radius-full)',
    padding: '2px 8px',
    textTransform: 'uppercase',
  },
  previewTraceNotApplied: {
    fontSize: '9px',
    fontWeight: 800,
    color: '#ff453a',
    backgroundColor: 'rgba(255, 69, 58, 0.1)',
    border: '1px solid rgba(255, 69, 58, 0.2)',
    borderRadius: 'var(--radius-full)',
    padding: '2px 8px',
    textTransform: 'uppercase',
  },
  previewTraceReason: {
    margin: '6px 0 0 0',
    fontSize: '11px',
    color: '#515154',
    lineHeight: '1.45',
  }
};
/* eslint-enable no-dupe-keys */

// Score bar fill color by value


/* Segmented card theme styling maps
const NEUTRAL_CARD_THEME = {
  bg: 'rgba(15, 23, 42, 0.02)',
  border: 'var(--color-border)',
  iconBg: 'rgba(15, 23, 42, 0.04)',
  iconColor: 'var(--color-text-secondary)',
  activeBg: 'rgba(15, 23, 42, 0.06)',
  activeBorder: 'var(--color-accent-ferrari)',
  glow: 'var(--glow-accent)'
};

const SECTION_CARD_THEMES = [
  // Indigo (Primary Accent)
  {
    bg: 'rgba(79, 70, 229, 0.02)',
    border: 'rgba(79, 70, 229, 0.12)',
    iconBg: 'rgba(79, 70, 229, 0.05)',
    iconColor: 'var(--color-accent-ferrari)',
    activeBg: 'rgba(79, 70, 229, 0.08)',
    activeBorder: 'var(--color-accent-ferrari)',
    glow: '0 0 0 3px var(--color-accent-glow)'
  },
  // Sky / Blue
  {
    bg: 'rgba(2, 132, 199, 0.02)',
    border: 'rgba(2, 132, 199, 0.12)',
    iconBg: 'rgba(2, 132, 199, 0.05)',
    iconColor: 'var(--color-sky-600)',
    activeBg: 'rgba(2, 132, 199, 0.08)',
    activeBorder: 'var(--color-sky-600)',
    glow: '0 0 0 3px rgba(2, 132, 199, 0.15)'
  },
  // Purple
  {
    bg: 'rgba(124, 58, 237, 0.02)',
    border: 'rgba(124, 58, 237, 0.12)',
    iconBg: 'rgba(124, 58, 237, 0.05)',
    iconColor: 'var(--color-purple-600)',
    activeBg: 'rgba(124, 58, 237, 0.08)',
    activeBorder: 'var(--color-purple-600)',
    glow: '0 0 0 3px rgba(124, 58, 237, 0.15)'
  },
  // Amber / Yellow
  {
    bg: 'rgba(217, 119, 6, 0.02)',
    border: 'rgba(217, 119, 6, 0.12)',
    iconBg: 'rgba(217, 119, 6, 0.05)',
    iconColor: 'var(--color-warning)',
    activeBg: 'rgba(217, 119, 6, 0.08)',
    activeBorder: 'var(--color-warning)',
    glow: '0 0 0 3px rgba(217, 119, 6, 0.15)'
  }
];
*/



export default Dashboard;
